// src/db.js
// ─────────────────────────────────────────────────────────────────
// Client-side data layer.  `db` is a chainable client exposing
// `.from(table).select/insert/update/upsert/delete/eq/order/…`
// so this file's ~240 helper functions read naturally, but under
// the hood every call is a POST to /api/db/query — no client library,
// no external service, just our own Keycloak-authed REST endpoint
// against RDS.
//
// The chainable shape was kept as a compat surface for the helper
// functions below (they'd otherwise all need rewrites).  New code
// can either use these helpers or hit /api/db/query directly.
//
// Companion files:
//   src/db-shim.js  — the chainable client itself
//   src/api.js      — REST wrapper (authFetch + response shape)
//   src/auth.js     — Keycloak SPA wrapper
//   src/storage.js  — S3 presigned-URL upload/download
//   src/realtime.js — SSE stream / live-events wrapper
//   api/db/query.js — server-side query executor + allow-list
// ─────────────────────────────────────────────────────────────────

import { createClient } from "./db-shim.js";
import * as authWrapper from "./auth.js";
import { api } from "./api.js";
import { realtime } from "./realtime.js";

// createClient() ignores its args (kept in the signature for
// call-site compat with libraries that expected url + key).
export const db = createClient();


// ── AUTH ─────────────────────────────────────────────────────────
// Authentication is Keycloak, entered from src/main.jsx.  The only
// thing the rest of the app talks to directly is `signOut()` (sidebar
// button + inactive-user bailout).  Everything else — sign-in, magic
// link, password reset, "change password" — happens on Keycloak's own
// pages.  There used to be a fat compatibility layer here re-exporting
// signInWithPassword / signInWithMagicLink / signInWithGoogle /
// requestPasswordReset / updateOwnPassword / getSession as no-op
// shims that redirected to Keycloak; nothing imported them any more,
// so they were deleted.  See git history for the shim implementation
// if you need to bring one back.

export const signOut = async () => {
  await authWrapper.logout();
};

/**
 * Load the full user profile from the users table.
 * Called after auth confirms a session exists.
 * Maps snake_case DB columns → camelCase app fields.
 */
const DEFAULT_ORG_ID = "d792ed77-5f05-4ecb-bc4d-7979948f0831";

const shapeProfile = (data) => ({
  id:               data.id,
  orgId:            data.org_id,
  email:            data.email,
  name:             data.name || data.email.split("@")[0],
  role:             data.role,
  userType:         data.user_type,
  company:          data.company || null,
  picture:          data.avatar_url,
  phone:            data.phone || "",
  isActive:         data.is_active,
  featureOverrides: data.feature_overrides || {},
  initials:         (data.name || data.email)
                      .split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2),
  accent:           roleAccent(data.role),
  status:           "online",
});

/**
 * Load a user profile by auth ID.
 * If no profile exists (first Google login), auto-create one with viewer role.
 * Pass the authenticated-user object so we can seed name/email/avatar.
 */
export const loadUserProfile = async (authUser) => {
  // Support passing just the ID for backwards compat
  const authUserId = typeof authUser === "string" ? authUser : authUser?.id;
  const authEmail  = typeof authUser === "string" ? null   : authUser?.email;
  const authMeta   = typeof authUser === "string" ? {}     : (authUser?.user_metadata || {});
  console.log("[loadUserProfile] start", { authUserId, authEmail });

  const byAuth = await db
    .from("users")
    .select(`id, org_id, email, name, role, user_type, avatar_url, phone, is_active, feature_overrides, company`)
    .eq("auth_id", authUserId)
    .maybeSingle();
  console.log("[loadUserProfile] by-authId lookup", {
    hit: !!byAuth?.data,
    error: byAuth?.error?.message || null,
  });

  if (byAuth?.data) return shapeProfile(byAuth.data);

  if (!authEmail) {
    console.warn("[loadUserProfile] no email on token — cannot fall back to email lookup");
    return null;
  }

  // ── Existing profile with old auth_id (e.g. migrated from magic link) ──
  const byEmail = await db
    .from("users")
    .select("id, org_id, email, name, role, user_type, avatar_url, phone, is_active, feature_overrides, company")
    .eq("email", authEmail)
    .maybeSingle();
  console.log("[loadUserProfile] by-email lookup", {
    hit: !!byEmail?.data,
    error: byEmail?.error?.message || null,
  });

  if (byEmail?.data) {
    // Re-link their profile to the new Keycloak auth_id (sub).
    const relink = await db.from("users").update({ auth_id: authUserId }).eq("id", byEmail.data.id);
    console.log("[loadUserProfile] re-linked auth_id for", authEmail, {
      error: relink?.error?.message || null,
    });
    return shapeProfile(byEmail.data);
  }

  // ── Brand new user — create profile ────────────────────
  const name = authMeta.full_name || authMeta.name || authEmail.split("@")[0];
  console.log("[loadUserProfile] no existing row — inserting new");

  const { data: newUser, error: insertError } = await db
    .from("users")
    .insert({
      auth_id:    authUserId,
      org_id:     DEFAULT_ORG_ID,
      email:      authEmail,
      name,
      role:       "viewer",
      user_type:  "internal",
      avatar_url: authMeta.avatar_url || authMeta.picture || null,
      is_active:  true,
    })
    .select()
    .single();

  if (insertError) {
    console.error("[loadUserProfile] Auto-create failed:", insertError.message, insertError);
    return null;
  }

  console.log("[loadUserProfile] Created new profile for", authEmail);
  return shapeProfile(newUser);
};

/**
 * Update last_seen timestamp on the user record.  Used both once at
 * login (to record "this account was active") and as the heartbeat
 * signal driving Messenger presence — anyone whose `users.last_seen`
 * is within the last ~2 minutes is considered "online".
 */
export const touchLastSeen = async (userId) => {
  await db
    .from("users")
    .update({ last_seen: new Date().toISOString() })
    .eq("id", userId);
};

/**
 * Presence poll — returns every user in the org whose last_seen is
 * within `sinceSeconds` (default 120s).  The client calls this on a
 * 30s interval and derives the green-dot Set from the result.
 *
 * Excludes the caller themselves — the UI doesn't need a green dot
 * for the current user.
 */
export const loadOnlineUsers = async (orgId, sinceSeconds = 120) => {
  const cutoff = new Date(Date.now() - sinceSeconds * 1000).toISOString();
  const { data, error } = await db
    .from("users")
    .select("id")
    .eq("org_id", orgId)
    .gte("last_seen", cutoff);
  if (error) { console.error("loadOnlineUsers:", error.message); return []; }
  return (data || []).map(u => u.id);
};

// ── NUGGET DB HELPERS ─────────────────────────────────────────────

/**
 * Load all nuggets for this org, newest first.
 * Maps DB snake_case columns → camelCase app fields.
 */
export const loadNuggets = async (orgId) => {
  const { data, error } = await db
    .from("nuggets")
    .select("*")
    .eq("org_id", orgId)
    .order("created_at", { ascending: false });

  if (error) { console.error("loadNuggets:", error.message); return null; }

  return data.map(dbToNugget);
};

/**
 * Save (upsert) a single nugget to the database.
 */
export const saveNugget = async (nugget, orgId) => {
  const { error } = await db
    .from("nuggets")
    .upsert(nuggetToDb(nugget, orgId), { onConflict: "id" });

  if (error) console.error("saveNugget ERROR:", error.message, "| code:", error.code, "| nugget id:", nugget.id, "| orgId:", orgId);
  return !error;
};

/**
 * Delete a nugget by ID.
 */
export const deleteNugget = async (id) => {
  const { error } = await db.from("nuggets").delete().eq("id", id);
  if (error) console.error("deleteNugget:", error.message);
  return !error;
};

// ── COMPANY & TYPE HELPERS ────────────────────────────────────────

export const loadCompanies = async (orgId) => {
  const { data } = await db
    .from("companies")
    .select("name")
    .eq("org_id", orgId)
    .order("name");
  return data?.map(c => c.name) ?? [];
};

export const addCompany = async (name, orgId) => {
  const { error } = await db
    .from("companies")
    .upsert({ name, org_id: orgId }, { onConflict: "org_id,name" });
  if (error) console.error("addCompany:", error.message);
};

// Delete a company by name (org-scoped). Also clears any users.company
// references so deleted-company tags don't linger on user rows.
export const deleteCompany = async (name, orgId) => {
  const { error: delErr } = await db
    .from("companies")
    .delete()
    .eq("org_id", orgId)
    .eq("name", name);
  if (delErr) { console.error("deleteCompany:", delErr.message); return false; }
  // Clear company assignment on any users that referenced it.
  await db
    .from("users")
    .update({ company: null })
    .eq("org_id", orgId)
    .eq("company", name);
  return true;
};

export const loadNuggetTypes = async (orgId) => {
  const { data } = await db
    .from("nugget_types")
    .select("name")
    .eq("org_id", orgId)
    .order("name");
  return data?.map(t => t.name) ?? [];
};

export const addNuggetType = async (name, orgId) => {
  const { error } = await db
    .from("nugget_types")
    .upsert({ name, org_id: orgId }, { onConflict: "org_id,name" });
  if (error) console.error("addNuggetType:", error.message);
};

// ── USERS LIST ────────────────────────────────────────────────────

// Admin-panel variant — returns ALL users (active + inactive) so deactivated
// accounts can be reactivated from the UI.
export const loadAllOrgUsers = async (orgId) => {
  const { data, error } = await db
    .from("users")
    .select("id, email, name, role, user_type, avatar_url, phone, feature_overrides, company, is_active, auth_id")
    .eq("org_id", orgId)
    .order("name");
  if (error) { console.error('loadAllOrgUsers:', error.message); return []; }
  return (data || []).map(u => ({
    id:               u.id,
    name:             u.name || u.email.split("@")[0],
    email:            u.email,
    role:             u.role,
    userType:         u.user_type,
    company:          u.company || null,
    phone:            u.phone || "",
    isActive:         u.is_active,
    authId:           u.auth_id || null,
    picture:          u.avatar_url,
    initials:         (u.name || u.email).split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2),
    accent:           roleAccent(u.role),
    status:           u.is_active ? "offline" : "inactive",
    featureOverrides: u.feature_overrides || {},
  }));
};

export const loadOrgUsers = async (orgId) => {
  const { data } = await db
    .from("users")
    .select("id, email, name, role, user_type, avatar_url, phone, feature_overrides, company")
    .eq("org_id", orgId)
    .eq("is_active", true)
    .order("name");

  return (data ?? []).map(u => ({
    id:              u.id,
    name:            u.name || u.email.split("@")[0],
    email:           u.email,
    role:            u.role,
    userType:        u.user_type,
    company:         u.company || null,
    phone:           u.phone || "",
    picture:         u.avatar_url,
    initials:        (u.name || u.email).split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2),
    accent:          roleAccent(u.role),
    status:          "offline",
    featureOverrides: u.feature_overrides || {},
  }));
};

// ── AUDIT LOG ─────────────────────────────────────────────────────

export const writeAuditLog = async ({ orgId, userId, action, entityType, entityId, newValue }) => {
  await db.from("audit_log").insert({
    org_id:      orgId,
    user_id:     userId,
    action,
    entity_type: entityType,
    entity_id:   entityId,
    new_value:   newValue ?? null,
  });
};

// ── FIELD MAPPERS (DB ↔ App) ──────────────────────────────────────

export function dbToNugget(d) {
  return {
    id:           d.id,
    title:        d.title,
    desc:         d.description || "",
    priority:     d.priority,
    col:          d.col,
    ownerId:      d.owner_id,
    assigneeId:   d.assignee_id,
    reporterId:   d.reporter_id,
    tags:         d.tags || [],
    visibility:   d.visibility,
    sharedWith:   d.shared_with || [],
    revenueValue: d.revenue_value,
    company:      d.company || "",
    nuggetType:   d.nugget_type || "Feature",
    source:       d.source || "web",
    ampCompanyId: d.amp_company_id,
    ampProjectId: d.amp_project_id,
    sladue:       d.sla_due_at,
    resolvedAt:   d.resolved_at,
    satisfaction: d.satisfaction,
    age:          timeAgo(d.created_at),
    createdAt:    d.created_at,
  };
}

function nuggetToDb(n, orgId) {
  return {
    id:             n.id,
    org_id:         orgId,
    title:          n.title,
    description:    n.desc || "",
    priority:       n.priority,
    col:            n.col,
    owner_id:       n.ownerId    || null,
    assignee_id:    n.assigneeId || null,
    reporter_id:    n.reporterId || null,
    tags:           n.tags || [],
    visibility:     n.visibility || "team",
    shared_with:    n.sharedWith || [],
    revenue_value:  n.revenueValue || "medium",
    company:        n.company || "",
    nugget_type:    n.nuggetType || "Feature",
    source:         n.source || "web",
    amp_company_id: n.ampCompanyId || null,
    amp_project_id: n.ampProjectId || null,
  };
}

function roleAccent(role) {
  return {
    super_admin: "#EF4444",
    admin:       "#E87722",
    developer:   "#60A5FA",
    viewer:      "#6B7280",
    org_admin:   "#A78BFA",
    org_member:  "#34D399",
    org_viewer:  "#6B7280",
  }[role] || "#E87722";
}

function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── NOTIFICATION SETTINGS ─────────────────────────────────────────

export const loadNotificationSettings = async (orgId) => {
  const { data, error } = await db
    .from("notification_settings")
    .select("*")
    .eq("org_id", orgId)
    .single();
  if (error) console.error("loadNotificationSettings:", error.message);
  return data ?? null;
};

export const saveNotificationSettings = async (orgId, settings) => {
  const { error } = await db
    .from("notification_settings")
    .upsert({ ...settings, org_id: orgId }, { onConflict: "org_id" });
  if (error) console.error("saveNotificationSettings:", error.message);
  return !error;
};

// ── SEND NOTIFICATIONS (via /api endpoints) ───────────────────────

// PATH B — the send-email / send-sms server endpoints are now
// /api/send-email and /api/send-sms (see api/_mailer.js, api/_sms.js).
// We use authFetch (Keycloak Bearer token) rather than the old anon key.
// Endpoints return HTTP 200 with { success:false, skipped:true, error }
// for "configured to skip" (e.g. notification_settings not set), which we
// pass through so background callers can silently bail via `result.skipped`.
const invokeFunction = async (name, body) => {
  const resp = await authWrapper.authFetch(`/api/${name}`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(body),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const msg = data?.error || `HTTP ${resp.status}`;
    console.error(`${name} error:`, msg);
    return { success: false, error: msg };
  }
  // Body may explicitly report success:false (e.g. skipped). Propagate verbatim.
  if (data && data.success === false) {
    if (!data.skipped) console.error(`${name} error:`, data.error);
    return { success: false, error: data.error, skipped: !!data.skipped, reason: data.reason };
  }
  return { success: true, data };
};

export const sendSMS = ({ to, message, orgId, nuggetId = null }) =>
  invokeFunction("send-sms", { to, message, org_id: orgId, nugget_id: nuggetId });

export const sendEmail = ({ to, subject, html, orgId, nuggetId = null, attachments = null }) =>
  invokeFunction("send-email", {
    to,
    subject,
    html,
    org_id: orgId,
    nugget_id: nuggetId,
    ...(attachments ? { attachments } : {}),
  });

// ── Proposal Coach — opportunities + notes ────────────────────
//
// Pipeline-driven precursor to the Deal Room. Pre-CRM notes flow
// into an opportunity, which progresses through stages toward a
// proposal. Org-scoped RLS.
export const loadOpportunities = async (orgId) => {
  const { data, error } = await db
    .from('opportunities')
    .select('*')
    .eq('org_id', orgId)
    .order('updated_at', { ascending: false });
  if (error) { console.error('loadOpportunities:', error.message); return []; }
  return data || [];
};

export const createOpportunity = async (orgId, userId, fields) => {
  const payload = {
    org_id:        orgId,
    owner_user_id: userId,
    name:          fields.name?.trim() || 'New opportunity',
    client_name:   fields.client_name?.trim() || null,
    source:        fields.source || null,
    stage:         fields.stage || 'note',
    estimated_value: fields.estimated_value || null,
    decision_date: fields.decision_date || null,
    scope_summary: fields.scope_summary?.trim() || null,
    // Optional fields — only include when the caller supplies them so
    // we don't overwrite DB defaults / other columns with nulls.
    ...(fields.crm_deal_id         ? { crm_deal_id:         fields.crm_deal_id }         : {}),
    ...(fields.artifact_profile_id ? { artifact_profile_id: fields.artifact_profile_id } : {}),
    ...(fields.primary_contact_id  ? { primary_contact_id:  fields.primary_contact_id }  : {}),
    ...(fields.client_priorities   ? { client_priorities:   fields.client_priorities?.trim() } : {}),
    ...(fields.currency            ? { currency:            fields.currency }            : {}),
  };
  const { data, error } = await db
    .from('opportunities')
    .insert(payload)
    .select()
    .single();
  if (error) { console.error('createOpportunity:', error.message); return { error: error.message }; }
  return { row: data };
};

// Find an opportunity that was already seeded from a given CRM deal,
// so the "Open in Proposal Coach" button stays idempotent — second
// click jumps to the existing opportunity instead of creating a
// duplicate.
export const findOpportunityByCrmDealId = async (orgId, crmDealId) => {
  if (!crmDealId) return null;
  const { data, error } = await db
    .from('opportunities')
    .select('*')
    .eq('org_id', orgId)
    .eq('crm_deal_id', crmDealId)
    .maybeSingle();
  if (error) { console.error('findOpportunityByCrmDealId:', error.message); return null; }
  return data;
};

export const updateOpportunity = async (id, patch) => {
  const { error } = await db
    .from('opportunities')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) { console.error('updateOpportunity:', error.message); return false; }
  return true;
};

export const deleteOpportunity = async (id) => {
  const { error } = await db.from('opportunities').delete().eq('id', id);
  if (error) { console.error('deleteOpportunity:', error.message); return false; }
  return true;
};

export const loadOpportunityNotes = async (opportunityId) => {
  const { data, error } = await db
    .from('opportunity_notes')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('created_at', { ascending: false });
  if (error) { console.error('loadOpportunityNotes:', error.message); return []; }
  return data || [];
};

export const addOpportunityNote = async (opportunityId, userId, fields) => {
  const payload = {
    opportunity_id: opportunityId,
    author_user_id: userId,
    note_type:      fields.note_type || 'observation',
    content:        fields.content?.trim(),
    attendees:      fields.attendees?.trim() || null,
    note_date:      fields.note_date || new Date().toISOString().slice(0, 10),
  };
  if (!payload.content) return { error: 'content required' };
  const { data, error } = await db
    .from('opportunity_notes')
    .insert(payload)
    .select()
    .single();
  if (error) { console.error('addOpportunityNote:', error.message); return { error: error.message }; }
  return { row: data };
};

export const deleteOpportunityNote = async (id) => {
  const { error } = await db.from('opportunity_notes').delete().eq('id', id);
  if (error) { console.error('deleteOpportunityNote:', error.message); return false; }
  return true;
};

// ── Proposal Library (org-wide, internal-only) ─────────────────
// Reusable knowledge base of past proposals, case studies, etc.
// Tagged by sector / client / document_type so AI Coach sessions can
// scope down to only the relevant slice.

// Load specific library docs by id (used for pinned references).
// Preserves order of the input ids.
export const loadProposalLibraryByIds = async (orgId, ids) => {
  if (!ids?.length) return [];
  const { data, error } = await db
    .from('proposal_library')
    .select('*')
    .eq('org_id', orgId)
    .in('id', ids);
  if (error) { console.error('loadProposalLibraryByIds:', error.message); return []; }
  const byId = new Map((data || []).map(d => [d.id, d]));
  return ids.map(id => byId.get(id)).filter(Boolean);
};

export const loadProposalLibrary = async (orgId, { sectors, clients, documentType, search } = {}) => {
  let q = db
    .from('proposal_library')
    .select('*')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });
  if (documentType)              q = q.eq('document_type', documentType);
  if (sectors && sectors.length) q = q.overlaps('sectors', sectors);
  if (clients && clients.length) q = q.overlaps('clients', clients);
  if (search)                    q = q.ilike('title', `%${search}%`);
  const { data, error } = await q;
  if (error) { console.error('loadProposalLibrary:', error.message); return []; }
  return data || [];
};

export const uploadProposalLibraryFile = async (orgId, userId, file, meta = {}) => {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path     = `${orgId}/${Date.now()}-${safeName}`;
  const { error: upErr } = await db.storage
    .from('proposal-library')
    .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
  if (upErr) { console.error('uploadProposalLibraryFile storage:', upErr.message); return { error: upErr.message }; }
  const payload = {
    org_id:        orgId,
    uploaded_by:   userId,
    title:         meta.title?.trim() || file.name,
    description:   meta.description?.trim() || null,
    document_type: meta.document_type || null,
    sectors:       Array.isArray(meta.sectors) ? meta.sectors : [],
    clients:       Array.isArray(meta.clients) ? meta.clients : [],
    geo_scope:     Array.isArray(meta.geo_scope) ? meta.geo_scope : [],
    outcome:       meta.outcome || null,
    tags:          Array.isArray(meta.tags) ? meta.tags : [],
    filename:      file.name,
    storage_path:  path,
    mime_type:     file.type,
    size_bytes:    file.size,
  };
  const { data, error } = await db
    .from('proposal_library')
    .insert(payload)
    .select()
    .single();
  if (error) { console.error('uploadProposalLibraryFile row:', error.message); return { error: error.message }; }
  return { row: data };
};

export const addProposalLibraryUrl = async (orgId, userId, meta) => {
  const payload = {
    org_id:        orgId,
    uploaded_by:   userId,
    title:         meta.title?.trim() || meta.url || 'Untitled',
    description:   meta.description?.trim() || null,
    document_type: meta.document_type || null,
    sectors:       Array.isArray(meta.sectors) ? meta.sectors : [],
    clients:       Array.isArray(meta.clients) ? meta.clients : [],
    geo_scope:     Array.isArray(meta.geo_scope) ? meta.geo_scope : [],
    tags:          Array.isArray(meta.tags) ? meta.tags : [],
    url:           meta.url,
  };
  const { data, error } = await db
    .from('proposal_library')
    .insert(payload)
    .select()
    .single();
  if (error) { console.error('addProposalLibraryUrl:', error.message); return { error: error.message }; }
  return { row: data };
};

export const updateProposalLibrary = async (id, patch) => {
  const { error } = await db
    .from('proposal_library')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) { console.error('updateProposalLibrary:', error.message); return false; }
  return true;
};

export const deleteProposalLibrary = async (row) => {
  if (row.storage_path) {
    await db.storage.from('proposal-library').remove([row.storage_path]);
  }
  const { error } = await db.from('proposal_library').delete().eq('id', row.id);
  if (error) { console.error('deleteProposalLibrary:', error.message); return false; }
  return true;
};

export const setProposalLibraryAiEnabled = async (docId, enable) => {
  const { data: { session } } = await db.auth.getSession();
  const token = session?.access_token;
  if (!token) return { error: 'not signed in' };
  const resp = await fetch('/api/proposal-library-prepare-ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ document_id: docId, enable: !!enable }),
  });
  let body = {};
  try { body = await resp.json(); } catch {}
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body;
};

// ── Opportunity Artifacts (RFP intake + deliverables checklist) ─

export const loadOpportunityArtifacts = async (opportunityId) => {
  const { data, error } = await db
    .from('opportunity_artifacts')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('direction', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) { console.error('loadOpportunityArtifacts:', error.message); return []; }
  return data || [];
};

export const createOpportunityArtifact = async (opportunityId, fields) => {
  const payload = {
    opportunity_id: opportunityId,
    direction:      fields.direction || 'outbound',
    artifact_type:  fields.artifact_type,
    name:           fields.name?.trim() || 'Untitled artifact',
    description:    fields.description?.trim() || null,
    status:         fields.status || 'pending',
    due_date:       fields.due_date || null,
    assignee_user_id: fields.assignee_user_id || null,
    notes:          fields.notes?.trim() || null,
  };
  const { data, error } = await db
    .from('opportunity_artifacts')
    .insert(payload)
    .select()
    .single();
  if (error) { console.error('createOpportunityArtifact:', error.message); return { error: error.message }; }
  return { row: data };
};

export const updateOpportunityArtifact = async (id, patch) => {
  const { error } = await db
    .from('opportunity_artifacts')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) { console.error('updateOpportunityArtifact:', error.message); return { error: error.message }; }
  return { ok: true };
};

export const deleteOpportunityArtifact = async (row) => {
  if (row.storage_path) {
    await db.storage.from('proposal-library').remove([row.storage_path]);
  }
  const { error } = await db.from('opportunity_artifacts').delete().eq('id', row.id);
  if (error) { console.error('deleteOpportunityArtifact:', error.message); return false; }
  return true;
};

// Upload a file to attach to an existing artifact row. Stored in the
// proposal-library bucket under a per-opportunity prefix so files
// stay organized and the existing internal-only storage RLS applies.
export const uploadOpportunityArtifactFile = async (orgId, opportunityId, artifactId, file) => {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path     = `${orgId}/opp-${opportunityId}/${Date.now()}-${safeName}`;
  const { error: upErr } = await db.storage
    .from('proposal-library')
    .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
  if (upErr) { console.error('uploadOpportunityArtifactFile storage:', upErr.message); return { error: upErr.message }; }
  const { error: rowErr } = await db
    .from('opportunity_artifacts')
    .update({
      filename:     file.name,
      storage_path: path,
      mime_type:    file.type,
      size_bytes:   file.size,
      updated_at:   new Date().toISOString(),
    })
    .eq('id', artifactId);
  if (rowErr) { console.error('uploadOpportunityArtifactFile row:', rowErr.message); return { error: rowErr.message }; }
  return { ok: true, path };
};

export const setOpportunityArtifactAiEnabled = async (artifactId, enable) => {
  const { data: { session } } = await db.auth.getSession();
  const token = session?.access_token;
  if (!token) return { error: 'not signed in' };
  const resp = await fetch('/api/opportunity-artifact-prepare-ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ artifact_id: artifactId, enable: !!enable }),
  });
  let body = {};
  try { body = await resp.json(); } catch {}
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body;
};

// Default outbound deliverables seeded on opportunity creation.
// User can delete / mark N/A what doesn't apply. Mirrors what a
// typical commercial-services proposal package looks like.
export const DEFAULT_OUTBOUND_ARTIFACTS = [
  { artifact_type: 'cover_letter',        name: 'Cover letter' },
  { artifact_type: 'exec_summary',        name: 'Executive summary' },
  { artifact_type: 'technical_approach',  name: 'Technical approach' },
  { artifact_type: 'pricing',             name: 'Pricing sheet' },
  { artifact_type: 'org_chart',           name: 'Org chart' },
  { artifact_type: 'resume',              name: 'Key staff resumes' },
  { artifact_type: 'past_performance',    name: 'Past performance references' },
  { artifact_type: 'capability_statement', name: 'Capability statement' },
  { artifact_type: 'insurance_cert',      name: 'Insurance certificate' },
];

export const seedDefaultOutboundArtifacts = async (opportunityId) => {
  const rows = DEFAULT_OUTBOUND_ARTIFACTS.map(a => ({
    opportunity_id: opportunityId,
    direction:      'outbound',
    artifact_type:  a.artifact_type,
    name:           a.name,
    status:         'pending',
  }));
  const { error } = await db.from('opportunity_artifacts').insert(rows);
  if (error) console.error('seedDefaultOutboundArtifacts:', error.message);
};

// ── Proposal Workflow: members, sections, sign-offs ────────────

export const PROPOSAL_SECTION_TYPES = [
  { id: 'cover_letter',         label: 'Cover letter' },
  { id: 'exec_summary',         label: 'Executive summary' },
  { id: 'technical_approach',   label: 'Technical approach' },
  { id: 'pricing_narrative',    label: 'Pricing narrative' },
  { id: 'staffing_plan',        label: 'Staffing plan' },
  { id: 'timeline',             label: 'Timeline / schedule' },
  { id: 'past_performance',     label: 'Past performance' },
  { id: 'compliance',           label: 'Compliance / certifications' },
  { id: 'appendix',             label: 'Appendix' },
  { id: 'other',                label: 'Other' },
];

export const PROPOSAL_CAPACITIES = [
  { id: 'contributor',     label: 'Contributor', desc: 'Writes / edits sections' },
  { id: 'approver',        label: 'Approver',    desc: 'Signs off on assigned sections' },
  { id: 'final_approver',  label: 'Final approver', desc: 'Last-tier sign-off (budget / pricing / legal)' },
];

// Default section seeds — same logic as artifact seeding. New
// opportunities get a starter set so the proposal isn't a blank page.
export const DEFAULT_PROPOSAL_SECTIONS = [
  { section_type: 'cover_letter',       title: 'Cover letter',         sequence_idx: 10 },
  { section_type: 'exec_summary',       title: 'Executive summary',    sequence_idx: 20 },
  { section_type: 'technical_approach', title: 'Technical approach',   sequence_idx: 30 },
  { section_type: 'staffing_plan',      title: 'Staffing plan',        sequence_idx: 40 },
  { section_type: 'timeline',           title: 'Timeline / schedule',  sequence_idx: 50 },
  { section_type: 'pricing_narrative',  title: 'Pricing narrative',    sequence_idx: 60 },
  { section_type: 'past_performance',   title: 'Past performance',     sequence_idx: 70 },
  { section_type: 'compliance',         title: 'Compliance',           sequence_idx: 80 },
];

// ─── Members ────────────────────────────────────────────────────
export const loadProposalMembers = async (opportunityId) => {
  const { data, error } = await db
    .from('proposal_members')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('created_at', { ascending: true });
  if (error) { console.error('loadProposalMembers:', error.message); return []; }
  return data || [];
};

export const upsertProposalMember = async (opportunityId, userId, fields) => {
  const payload = {
    opportunity_id: opportunityId,
    user_id:        userId,
    role:           fields.role?.trim() || 'contributor',
    capacities:     Array.isArray(fields.capacities) && fields.capacities.length ? fields.capacities : ['contributor'],
    section_scope:  Array.isArray(fields.section_scope) ? fields.section_scope : [],
  };
  const { data, error } = await db
    .from('proposal_members')
    .upsert(payload, { onConflict: 'opportunity_id,user_id' })
    .select()
    .single();
  if (error) { console.error('upsertProposalMember:', error.message); return { error: error.message }; }
  return { row: data };
};

export const removeProposalMember = async (id) => {
  const { error } = await db.from('proposal_members').delete().eq('id', id);
  if (error) { console.error('removeProposalMember:', error.message); return false; }
  return true;
};

// ─── My approvals queue ─────────────────────────────────────────
// Returns proposal sections that are currently awaiting THIS user's
// signoff — i.e. sections in a review state where the user is a
// proposal_member on the parent opportunity with a capacity that
// matches the review type.
//
// Review-state → required capacity:
//   contributor_review → 'contributor' (or 'final_approver'; final
//                        approvers implicitly satisfy contributor
//                        review too, matching the UI's convention)
//   final_review       → 'final_approver'
//
// Returns objects shaped for the Mission Control widget:
//   { section_id, section_title, section_status, opportunity_id,
//     opportunity_name, review_kind: 'contributor' | 'final',
//     updated_at }
export const loadMyPendingApprovals = async (userId, orgId) => {
  if (!userId || !orgId) return [];
  // 1) Every opportunity where I'm a member with any capacity.
  const { data: mem } = await db
    .from('proposal_members')
    .select('opportunity_id, capacities')
    .eq('user_id', userId);
  const capsByOpp = new Map((mem || []).map(r => [r.opportunity_id, r.capacities || []]));
  const oppIds = [...capsByOpp.keys()];
  if (!oppIds.length) return [];

  // 2) Every section in those opportunities that's in a review state.
  const { data: sections } = await db
    .from('proposal_sections')
    .select('id, title, status, opportunity_id, updated_at')
    .in('opportunity_id', oppIds)
    .in('status', ['contributor_review', 'final_review'])
    .order('updated_at', { ascending: false });
  if (!sections?.length) return [];

  // 3) Filter: only include sections whose review type matches one
  //    of MY capacities on that opportunity.
  const relevant = sections.filter(s => {
    const caps = capsByOpp.get(s.opportunity_id) || [];
    if (s.status === 'final_review')       return caps.includes('final_approver');
    if (s.status === 'contributor_review') return caps.includes('contributor') || caps.includes('final_approver');
    return false;
  });
  if (!relevant.length) return [];

  // 4) Filter out any sections I've already signed off on at the
  //    current review capacity — a decision row exists in
  //    proposal_signoffs for me at this capacity.
  const sectionIds = relevant.map(s => s.id);
  const { data: mySignoffs } = await db
    .from('proposal_signoffs')
    .select('section_id, capacity_used, decision')
    .eq('user_id', userId)
    .in('section_id', sectionIds);
  const doneByCap = new Set((mySignoffs || []).map(s => `${s.section_id}:${s.capacity_used}`));
  const pending = relevant.filter(s => {
    const cap = s.status === 'final_review' ? 'final_approver' : 'contributor';
    return !doneByCap.has(`${s.id}:${cap}`);
  });
  if (!pending.length) return [];

  // 5) Hydrate with opportunity names.
  const uniqueOppIds = [...new Set(pending.map(s => s.opportunity_id))];
  const { data: opps } = await db
    .from('opportunities')
    .select('id, name')
    .in('id', uniqueOppIds);
  const oppNameById = new Map((opps || []).map(o => [o.id, o.name]));

  return pending.map(s => ({
    section_id:       s.id,
    section_title:    s.title,
    section_status:   s.status,
    opportunity_id:   s.opportunity_id,
    opportunity_name: oppNameById.get(s.opportunity_id) || '(unknown opportunity)',
    review_kind:      s.status === 'final_review' ? 'final' : 'contributor',
    updated_at:       s.updated_at,
  }));
};

// Approve / reject a section from the Mission Control queue.
// Writes to proposal_signoffs + advances proposal_sections.status.
// State machine:
//   contributor_review + approve  → contributor_approved
//   contributor_approved + submit  → final_review           (elsewhere)
//   final_review + approve         → final_approved
//   any                + reject    → rejected
//   any                + changes   → draft   (author fixes then resubmits)
export const submitProposalSectionDecision = async ({ sectionId, userId, capacity, decision, comments = null }) => {
  if (!sectionId || !userId) return { error: 'sectionId + userId required' };
  if (!['contributor', 'final_approver'].includes(capacity)) return { error: 'bad capacity' };
  if (!['approved', 'rejected', 'changes_requested'].includes(decision)) return { error: 'bad decision' };

  // Record the signoff.  A user can decide once per (section, capacity);
  // reversing a decision (changing mind) is done as a delete-then-
  // insert since there's no composite unique index to hang ON CONFLICT
  // off of.  Two round-trips but idempotent.
  await db.from('proposal_signoffs')
    .delete().eq('section_id', sectionId).eq('user_id', userId).eq('capacity_used', capacity);
  const { error: signoffErr } = await db.from('proposal_signoffs').insert({
    section_id:      sectionId,
    user_id:         userId,
    capacity_used:   capacity,
    decision,
    comments,
    decided_at:      new Date().toISOString(),
  });
  if (signoffErr) { console.error('submitProposalSectionDecision:', signoffErr.message); return { error: signoffErr.message }; }

  // Advance section status.
  let nextStatus;
  if (decision === 'approved') {
    nextStatus = capacity === 'contributor' ? 'contributor_approved' : 'final_approved';
  } else if (decision === 'rejected') {
    nextStatus = 'rejected';
  } else {
    nextStatus = 'draft'; // changes_requested → author picks it back up
  }
  const { error: statusErr } = await db
    .from('proposal_sections')
    .update({ status: nextStatus, updated_at: new Date().toISOString() })
    .eq('id', sectionId);
  if (statusErr) { console.error('submitProposalSectionDecision update:', statusErr.message); return { error: statusErr.message }; }

  return { row: { section_id: sectionId, status: nextStatus, decision } };
};

// ─── Sections ───────────────────────────────────────────────────
export const loadProposalSections = async (opportunityId) => {
  const { data, error } = await db
    .from('proposal_sections')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('sequence_idx', { ascending: true })
    .order('created_at',   { ascending: true });
  if (error) { console.error('loadProposalSections:', error.message); return []; }
  return data || [];
};

export const createProposalSection = async (opportunityId, fields) => {
  const payload = {
    opportunity_id: opportunityId,
    section_type:   fields.section_type || 'other',
    title:          fields.title?.trim() || 'Untitled section',
    content:        fields.content || '',
    primary_author_user_id: fields.primary_author_user_id || null,
    sequence_idx:   fields.sequence_idx ?? 100,
  };
  const { data, error } = await db
    .from('proposal_sections')
    .insert(payload)
    .select()
    .single();
  if (error) { console.error('createProposalSection:', error.message); return { error: error.message }; }
  return { row: data };
};

export const updateProposalSection = async (id, patch, opts = {}) => {
  const { editorUserId, source = 'edit' } = opts;
  const body = { ...patch, updated_at: new Date().toISOString() };
  // Stamp the source + editor so the snapshot trigger can attribute
  // the version row correctly. The trigger reads these fields off
  // the NEW row during UPDATE.
  if (editorUserId !== undefined) body.last_edited_by_user_id = editorUserId;
  if (source !== undefined)       body.last_edit_source       = source;
  const { error } = await db
    .from('proposal_sections')
    .update(body)
    .eq('id', id);
  if (error) { console.error('updateProposalSection:', error.message); return { error: error.message }; }
  return { ok: true };
};

// ── Inline images per section ─────────────────────────────────
// Stored in the existing proposal-library bucket (internal-only
// access, no public URLs). Storage path is namespaced by org +
// opportunity + section to keep buckets tidy.

export const loadProposalSectionImages = async (sectionId) => {
  const { data, error } = await db
    .from('proposal_section_images')
    .select('*')
    .eq('section_id', sectionId)
    .order('sequence_idx', { ascending: true })
    .order('created_at',   { ascending: true });
  if (error) { console.error('loadProposalSectionImages:', error.message); return []; }
  return data || [];
};

export const loadProposalSectionImagesByOpp = async (opportunityId) => {
  const { data, error } = await db
    .from('proposal_section_images')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('sequence_idx', { ascending: true });
  if (error) { console.error('loadProposalSectionImagesByOpp:', error.message); return []; }
  return data || [];
};

// Sign a temporary URL so the browser can render the image without
// making the bucket public. Cached aggressively at 1 hour.
export const getProposalImageSignedUrl = async (storagePath) => {
  if (!storagePath) return null;
  const { data, error } = await db.storage
    .from('proposal-library')
    .createSignedUrl(storagePath, 3600);
  if (error) { console.error('getProposalImageSignedUrl:', error.message); return null; }
  return data?.signedUrl || null;
};

export const uploadProposalSectionImage = async (orgId, opportunityId, sectionId, file, userId) => {
  if (!file) return { error: 'no file' };
  if (!file.type?.startsWith('image/')) return { error: 'Only image files (PNG, JPG, WEBP, GIF) are accepted.' };
  if (file.size > 10 * 1024 * 1024)     return { error: 'Image is larger than 10MB.' };
  const ext  = (file.name?.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
  const id   = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
  const path = `${orgId}/${opportunityId}/sections/${sectionId}/${id}.${ext}`;
  const { error: upErr } = await db.storage
    .from('proposal-library')
    .upload(path, file, { contentType: file.type, upsert: false });
  if (upErr) { console.error('uploadProposalSectionImage:', upErr.message); return { error: upErr.message }; }

  // Read image dimensions client-side so the PDF assembler can do
  // sensible sizing without re-loading every image.
  let width = null, height = null;
  try {
    const dims = await readImageDimensions(file);
    width = dims.width; height = dims.height;
  } catch {}

  const { data, error } = await db
    .from('proposal_section_images')
    .insert({
      section_id:          sectionId,
      opportunity_id:      opportunityId,
      storage_path:        path,
      filename:            file.name || `${id}.${ext}`,
      mime_type:           file.type || null,
      size_bytes:          file.size || null,
      width, height,
      uploaded_by_user_id: userId,
    })
    .select()
    .single();
  if (error) { console.error('uploadProposalSectionImage insert:', error.message); return { error: error.message }; }
  return { row: data };
};

export const updateProposalSectionImage = async (id, patch) => {
  const { error } = await db
    .from('proposal_section_images')
    .update(patch)
    .eq('id', id);
  if (error) { console.error('updateProposalSectionImage:', error.message); return { error: error.message }; }
  return { ok: true };
};

export const deleteProposalSectionImage = async (image) => {
  if (image.storage_path) {
    await db.storage.from('proposal-library').remove([image.storage_path]);
  }
  const { error } = await db.from('proposal_section_images').delete().eq('id', image.id);
  if (error) { console.error('deleteProposalSectionImage:', error.message); return false; }
  return true;
};

const readImageDimensions = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload  = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => reject(new Error('image-decode-failed'));
    img.src = e.target.result;
  };
  reader.onerror = () => reject(new Error('file-read-failed'));
  reader.readAsDataURL(file);
});

// Load every revision across EVERY section on this opportunity in
// one query — used by the top-level "all versions" modal so users
// can find their work without expanding each section individually.
export const loadAllSectionVersionsForOpp = async (opportunityId) => {
  const { data, error } = await db
    .from('proposal_section_versions')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('edited_at', { ascending: false });
  if (error) { console.error('loadAllSectionVersionsForOpp:', error.message); return []; }
  return hydrateWithUsers(data || [], 'edited_by_user_id');
};

// Load every prior revision of a section, newest first. Each row
// represents what the section looked like BEFORE the change that
// produced it (snapshot of OLD by the trigger).
export const loadProposalSectionVersions = async (sectionId) => {
  const { data, error } = await db
    .from('proposal_section_versions')
    .select('*')
    .eq('section_id', sectionId)
    .order('edited_at', { ascending: false });
  if (error) { console.error('loadProposalSectionVersions:', error.message); return []; }
  return data || [];
};

// Restore a prior version's content into the section. The snapshot
// trigger fires on this update too, so the current content gets
// preserved as a "restore" version automatically — no work lost.
export const restoreProposalSectionVersion = async (sectionId, version, userId) => {
  const { error } = await db
    .from('proposal_sections')
    .update({
      content:                version.content,
      title:                  version.title,
      last_edit_source:       'restore',
      last_edited_by_user_id: userId,
      updated_at:             new Date().toISOString(),
    })
    .eq('id', sectionId);
  if (error) { console.error('restoreProposalSectionVersion:', error.message); return { error: error.message }; }
  return { ok: true };
};

export const deleteProposalSection = async (id) => {
  const { error } = await db.from('proposal_sections').delete().eq('id', id);
  if (error) { console.error('deleteProposalSection:', error.message); return false; }
  return true;
};

export const seedDefaultProposalSections = async (opportunityId) => {
  const rows = DEFAULT_PROPOSAL_SECTIONS.map(s => ({
    opportunity_id: opportunityId,
    section_type:   s.section_type,
    title:          s.title,
    sequence_idx:   s.sequence_idx,
    status:         'draft',
    content:        '',
  }));
  const { error } = await db.from('proposal_sections').insert(rows);
  if (error) console.error('seedDefaultProposalSections:', error.message);
};

// ─── Sign-offs ──────────────────────────────────────────────────
export const loadProposalSignoffs = async (sectionId) => {
  const { data, error } = await db
    .from('proposal_signoffs')
    .select('*')
    .eq('section_id', sectionId)
    .order('decided_at', { ascending: false });
  if (error) { console.error('loadProposalSignoffs:', error.message); return []; }
  return data || [];
};

export const loadProposalSignoffsForOpp = async (opportunityId) => {
  // Get signoffs for every section on this opportunity in one query
  // so the section list can show counts without N+1.
  const { data: sections } = await db
    .from('proposal_sections')
    .select('id')
    .eq('opportunity_id', opportunityId);
  const ids = (sections || []).map(s => s.id);
  if (!ids.length) return [];
  const { data, error } = await db
    .from('proposal_signoffs')
    .select('*')
    .in('section_id', ids)
    .order('decided_at', { ascending: false });
  if (error) { console.error('loadProposalSignoffsForOpp:', error.message); return []; }
  return data || [];
};

// ── Phase 12: Outcomes (post-decision feedback capture) ───────────

export const OUTCOME_KINDS = [
  { id: 'won',         label: 'Won',         color: '#34d399' },
  { id: 'lost',        label: 'Lost',        color: '#f87171' },
  { id: 'no_decision', label: 'No decision', color: '#94a3b8' },
  { id: 'withdrawn',   label: 'Withdrawn',   color: '#a78bfa' },
];

export const loadOpportunityOutcome = async (opportunityId) => {
  const { data, error } = await db
    .from('opportunity_outcomes')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .maybeSingle();
  if (error) { console.error('loadOpportunityOutcome:', error.message); return null; }
  return data;
};

export const upsertOpportunityOutcome = async (opportunityId, fields, userId) => {
  const payload = {
    opportunity_id:           opportunityId,
    outcome:                  fields.outcome,
    decided_at:               fields.decided_at || null,
    awarded_value:            fields.awarded_value ? Number(fields.awarded_value) : null,
    awarded_currency:         fields.awarded_currency || null,
    winning_competitor_id:    fields.winning_competitor_id || null,
    winning_competitor_name:  fields.winning_competitor_name || null,
    reasons_we_won:           fields.reasons_we_won || null,
    reasons_we_lost:          fields.reasons_we_lost || null,
    client_feedback:          fields.client_feedback || null,
    lessons_learned:          fields.lessons_learned || null,
    captured_by_user_id:      userId,
    updated_at:               new Date().toISOString(),
  };
  const { data, error } = await db
    .from('opportunity_outcomes')
    .upsert(payload, { onConflict: 'opportunity_id' })
    .select()
    .single();
  if (error) { console.error('upsertOpportunityOutcome:', error.message); return { error: error.message }; }

  // Sync the opportunity stage to match the outcome so the pipeline
  // view reflects reality without forcing two saves.
  const stageMap = { won: 'won', lost: 'lost', no_decision: 'lost', withdrawn: 'lost' };
  const nextStage = stageMap[fields.outcome];
  if (nextStage) {
    await db.from('opportunities').update({ stage: nextStage }).eq('id', opportunityId);
  }
  return { row: data };
};

// Bundle a won proposal back into the org library as a precedent.
// Pulls the final-approved sections, stitches them together, creates
// a proposal_library row, then marks the outcome row saved.
export const saveOutcomeToLibrary = async (opportunityId, outcomeId, currentUser) => {
  const { data: opp } = await db
    .from('opportunities')
    .select('id, name, client_name, scope_summary, estimated_value')
    .eq('id', opportunityId)
    .maybeSingle();
  if (!opp) return { error: 'opportunity not found' };

  const { data: sections } = await db
    .from('proposal_sections')
    .select('title, section_type, content, ai_generated_content, sequence_idx, status')
    .eq('opportunity_id', opportunityId)
    .neq('status', 'rejected')
    .order('sequence_idx', { ascending: true });

  const body = (sections || []).map(s => {
    const content = s.content || s.ai_generated_content || '';
    return `## ${s.title}\n\n${content}`;
  }).join('\n\n');

  const libPayload = {
    org_id:        currentUser.orgId,
    title:         `${opp.name}${opp.client_name ? ` — ${opp.client_name}` : ''} (Won)`,
    description:   opp.scope_summary || null,
    document_type: 'past_proposal',
    sectors:       [],
    clients:       opp.client_name ? [opp.client_name] : [],
    tags:          ['precedent', 'won'],
    outcome:       'won',
    extracted_content: body,
    extracted_title:   `${opp.name} — Won proposal precedent`,
    extracted_summary: opp.scope_summary?.slice(0, 400) || null,
    ai_enabled:    true,
    ai_prepared_at: new Date().toISOString(),
    uploaded_by: currentUser.id,
  };
  const { data: libDoc, error: libErr } = await db
    .from('proposal_library')
    .insert(libPayload)
    .select()
    .single();
  if (libErr) { console.error('saveOutcomeToLibrary:', libErr.message); return { error: libErr.message }; }

  // Mark the outcome row saved + link the library doc.
  await db
    .from('opportunity_outcomes')
    .update({ saved_to_library: true, saved_to_library_doc_id: libDoc.id })
    .eq('id', outcomeId);

  return { libraryDoc: libDoc };
};

// ── Phase 11: Artifact requirement profiles ──────────────────────
// Org-level templates that drive opportunity seeding. Replaces the
// hard-coded DEFAULT_OUTBOUND_ARTIFACTS / DEFAULT_PROPOSAL_SECTIONS
// at the org level — users can define profiles per RFP category
// (Federal, State DOT, Enterprise SaaS, Academic Grant, etc.).

export const loadArtifactProfiles = async (orgId) => {
  const { data, error } = await db
    .from('artifact_profiles')
    .select('*')
    .eq('org_id', orgId)
    .order('is_default', { ascending: false })
    .order('name',       { ascending: true });
  if (error) { console.error('loadArtifactProfiles:', error.message); return []; }
  return data || [];
};

export const upsertArtifactProfile = async (orgId, fields, userId) => {
  const payload = {
    ...(fields.id ? { id: fields.id } : {}),
    org_id:                       orgId,
    name:                         fields.name?.trim() || 'Untitled profile',
    description:                  fields.description?.trim() || null,
    default_outbound_artifacts:   Array.isArray(fields.default_outbound_artifacts) ? fields.default_outbound_artifacts : [],
    default_proposal_sections:    Array.isArray(fields.default_proposal_sections)  ? fields.default_proposal_sections  : [],
    is_default:                   !!fields.is_default,
    created_by_user_id:           userId,
    updated_at:                   new Date().toISOString(),
  };
  const { data, error } = await db
    .from('artifact_profiles')
    .upsert(payload)
    .select()
    .single();
  if (error) { console.error('upsertArtifactProfile:', error.message); return { error: error.message }; }
  // If this profile was marked default, unset other defaults in the
  // same org. Partial unique index enforces single-default; do this
  // cleanup as a follow-up to avoid the conflict.
  if (data.is_default) {
    await db
      .from('artifact_profiles')
      .update({ is_default: false })
      .eq('org_id', orgId)
      .neq('id', data.id)
      .eq('is_default', true);
  }
  return { row: data };
};

export const deleteArtifactProfile = async (id) => {
  const { error } = await db.from('artifact_profiles').delete().eq('id', id);
  if (error) { console.error('deleteArtifactProfile:', error.message); return false; }
  return true;
};

// Apply a profile's seed lists to a freshly-created opportunity.
// Falls back to the hard-coded defaults when no profile provided.
export const seedOpportunityFromProfile = async (opportunityId, profile) => {
  const outbound = (profile?.default_outbound_artifacts?.length ? profile.default_outbound_artifacts : DEFAULT_OUTBOUND_ARTIFACTS)
    .map(a => ({
      opportunity_id: opportunityId,
      direction:      'outbound',
      artifact_type:  a.artifact_type,
      name:           a.name,
      status:         'pending',
    }));
  if (outbound.length) {
    const { error } = await db.from('opportunity_artifacts').insert(outbound);
    if (error) console.error('seedOpportunityFromProfile outbound:', error.message);
  }
  const sections = (profile?.default_proposal_sections?.length ? profile.default_proposal_sections : DEFAULT_PROPOSAL_SECTIONS)
    .map(s => ({
      opportunity_id: opportunityId,
      section_type:   s.section_type,
      title:          s.title,
      sequence_idx:   s.sequence_idx ?? 100,
      status:         'draft',
      content:        '',
    }));
  if (sections.length) {
    const { error } = await db.from('proposal_sections').insert(sections);
    if (error) console.error('seedOpportunityFromProfile sections:', error.message);
  }
};

// ── Phase 10: Competition view (per-opportunity competitor intel) ──

export const THREAT_LEVELS = [
  { id: 'low',       label: 'Low',       color: '#94a3b8' },
  { id: 'medium',    label: 'Medium',    color: '#fbbf24' },
  { id: 'high',      label: 'High',      color: '#f87171' },
  { id: 'incumbent', label: 'Incumbent', color: '#a78bfa' },
];

export const PRICING_POSTURES = [
  { id: 'unknown',  label: 'Unknown' },
  { id: 'undercut', label: 'Undercut us' },
  { id: 'match',    label: 'Match us' },
  { id: 'premium',  label: 'Premium / above us' },
];

export const loadOpportunityCompetitors = async (opportunityId) => {
  const { data, error } = await db
    .from('opportunity_competitors')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('threat_level', { ascending: false })
    .order('created_at',   { ascending: true });
  if (error) { console.error('loadOpportunityCompetitors:', error.message); return []; }
  return data || [];
};

export const upsertOpportunityCompetitor = async (opportunityId, fields, userId) => {
  const payload = {
    ...(fields.id ? { id: fields.id } : {}),
    opportunity_id:    opportunityId,
    name:              fields.name?.trim() || 'Unnamed competitor',
    threat_level:      fields.threat_level || 'medium',
    strengths:         fields.strengths    || null,
    weaknesses:        fields.weaknesses   || null,
    our_advantage:     fields.our_advantage || null,
    pricing_posture:   fields.pricing_posture || 'unknown',
    source:            fields.source       || null,
    notes:             fields.notes        || null,
    created_by_user_id: userId,
    updated_at:        new Date().toISOString(),
  };
  const { data, error } = await db
    .from('opportunity_competitors')
    .upsert(payload)
    .select()
    .single();
  if (error) { console.error('upsertOpportunityCompetitor:', error.message); return { error: error.message }; }
  return { row: data };
};

export const deleteOpportunityCompetitor = async (id) => {
  const { error } = await db.from('opportunity_competitors').delete().eq('id', id);
  if (error) { console.error('deleteOpportunityCompetitor:', error.message); return false; }
  return true;
};

// Apply a critique fix surgically to a section. Takes the section
// and a "suggested_fix" string from a weakness card, has the AI
// rewrite the section to address that specific issue, and persists.
// Version history captures the prior content automatically.
export const applyCritiqueFixToSection = async (sectionId, fix, opts = {}) => {
  const { issue, whyItMatters, model } = opts;
  const { data: { session } } = await db.auth.getSession();
  if (!session?.access_token) return { error: 'Not authenticated' };
  try {
    const res = await fetch('/api/opportunity-apply-fix', {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        section_id:     sectionId,
        issue:          issue || '',
        fix,
        why_it_matters: whyItMatters || '',
        model:          model || undefined,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { error: body.error || `HTTP ${res.status}` };
    return body;
  } catch (e) {
    return { error: e.message || 'network error' };
  }
};

// Phase 9: Critique mode — AI red-team of the assembled proposal.
export const runOpportunityCritique = async (opportunityId, opts = {}) => {
  const { model } = opts;
  const { data: { session } } = await db.auth.getSession();
  if (!session?.access_token) return { error: 'Not authenticated' };
  try {
    const res = await fetch('/api/opportunity-critique', {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        opportunity_id: opportunityId,
        model:          model || undefined,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { error: body.error || `HTTP ${res.status}` };
    return body;
  } catch (e) {
    return { error: e.message || 'network error' };
  }
};

// Phase 8: Gap radar — runs an AI sweep comparing inbound RFP
// against drafted proposal + outbound checklist. Persists findings
// to opportunities.gap_findings.
export const runOpportunityGapRadar = async (opportunityId, opts = {}) => {
  const { model } = opts;
  const { data: { session } } = await db.auth.getSession();
  if (!session?.access_token) return { error: 'Not authenticated' };
  try {
    const res = await fetch('/api/opportunity-gap-radar', {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        opportunity_id: opportunityId,
        model:          model || undefined,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { error: body.error || `HTTP ${res.status}` };
    return body;
  } catch (e) {
    return { error: e.message || 'network error' };
  }
};

// Phase 7: AI-scored win probability. Triggers a one-shot Claude
// assessment of the opportunity and writes the score + rationale +
// strengths/gaps/next_actions back to the opportunities row.
export const scoreOpportunityWin = async (opportunityId, opts = {}) => {
  const { linkedContext, libraryScope, model } = opts;
  const { data: { session } } = await db.auth.getSession();
  if (!session?.access_token) return { error: 'Not authenticated' };
  try {
    const res = await fetch('/api/opportunity-score-win', {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        opportunity_id: opportunityId,
        linked_context: linkedContext || null,
        library_scope:  libraryScope  || null,
        model:          model         || undefined,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { error: body.error || `HTTP ${res.status}` };
    return body;
  } catch (e) {
    return { error: e.message || 'network error' };
  }
};

// ── Reference-a-past-proposal (clone theme + directives + AI-visible template) ──

// Copy the visual theme from one opportunity to another. Overwrites
// the target's theme. Used when the user clones formatting from
// a previous proposal to match its brand / layout.
export const cloneOpportunityTheme = async (fromOppId, toOppId) => {
  const { data: src, error: readErr } = await db
    .from('opportunities')
    .select('theme')
    .eq('id', fromOppId)
    .maybeSingle();
  if (readErr || !src) return { error: readErr?.message || 'source opportunity not found' };
  const { error: writeErr } = await db
    .from('opportunities')
    .update({ theme: src.theme || null, updated_at: new Date().toISOString() })
    .eq('id', toOppId);
  if (writeErr) return { error: writeErr.message };
  return { ok: true, theme: src.theme || null };
};

// Copy AI directives from one opportunity to another. Directives are
// APPENDED to the target's existing list (with a lineage prefix so
// the team can tell which came from where) — that way the user can
// pull from multiple past proposals without wiping their own additions.
export const cloneOpportunityDirectives = async (fromOppId, toOppId, sourceLabel, userId) => {
  const { data: src } = await db
    .from('opportunity_ai_directives')
    .select('content')
    .eq('opportunity_id', fromOppId)
    .order('created_at', { ascending: true });
  const items = src || [];
  if (items.length === 0) return { ok: true, appended: 0 };
  const rows = items.map(d => ({
    opportunity_id:     toOppId,
    content:            sourceLabel ? `(from ${sourceLabel}) ${d.content}` : d.content,
    created_by_user_id: userId || null,
  }));
  const { error } = await db.from('opportunity_ai_directives').insert(rows);
  if (error) return { error: error.message };
  return { ok: true, appended: rows.length };
};

// ── Library L5: full-text search + saved searches ────────────────

// Full-text search via the search_tsv column populated by the
// trigger. Falls back to a naive title/summary substring search if
// the tsvector column isn't yet backfilled (fresh migration + old
// docs). Returns ranked results.
export const searchLibrary = async (orgId, { q, filters = {}, limit = 100 } = {}) => {
  if (!q?.trim()) {
    // No FTS query — just filter fetch
    let builder = db
      .from('proposal_library')
      .select('*')
      .eq('org_id', orgId);
    if (filters.doc_type) builder = builder.eq('document_type', filters.doc_type);
    if (filters.sectors?.length) builder = builder.overlaps('sectors', filters.sectors);
    if (filters.clients?.length) builder = builder.overlaps('clients', filters.clients);
    if (filters.tags?.length)    builder = builder.overlaps('tags',    filters.tags);
    if (filters.ai_only)         builder = builder.eq('ai_enabled', true);
    const { data, error } = await builder.order('updated_at', { ascending: false }).limit(limit);
    if (error) { console.error('searchLibrary (filters only):', error.message); return []; }
    return data || [];
  }

  // FTS via RPC on the tsvector column. Uses websearch_to_tsquery
  // so users can type "NOAA fisheries -oil" naturally.
  const { data, error } = await db.rpc('library_fts_search', {
    p_org_id: orgId,
    p_query:  q,
    p_limit:  limit,
    p_doc_type: filters.doc_type || null,
    p_sectors:  filters.sectors  || null,
    p_clients:  filters.clients  || null,
    p_tags:     filters.tags     || null,
    p_ai_only:  !!filters.ai_only,
  });
  if (error) {
    // RPC missing → fall back to substring search until the
    // library_fts_search function is added. Prevents the search
    // box breaking if the migration hasn't been fully applied.
    if (error.message?.includes('does not exist')) {
      console.warn('[searchLibrary] library_fts_search RPC missing — falling back to substring');
      const all = await loadProposalLibrary(orgId);
      const needle = q.trim().toLowerCase();
      return all.filter(d => {
        const hay = `${d.title || ''} ${d.extracted_title || ''} ${d.extracted_summary || ''} ${d.description || ''} ${(d.tags || []).join(' ')} ${(d.sectors || []).join(' ')} ${(d.clients || []).join(' ')}`.toLowerCase();
        return hay.includes(needle);
      }).slice(0, limit);
    }
    console.error('searchLibrary:', error.message);
    return [];
  }
  return data || [];
};

// Saved searches (per-user).
export const loadSavedSearches = async (userId) => {
  const { data, error } = await db
    .from('library_saved_searches')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) { console.error('loadSavedSearches:', error.message); return []; }
  return data || [];
};

export const saveSavedSearch = async (userId, orgId, fields) => {
  const payload = {
    ...(fields.id ? { id: fields.id } : {}),
    user_id:         userId,
    org_id:          orgId,
    name:            fields.name?.trim() || 'Untitled search',
    query:           fields.query || null,
    filter_doc_type: fields.filter_doc_type || null,
    filter_sectors:  fields.filter_sectors  || [],
    filter_clients:  fields.filter_clients  || [],
    filter_tags:     fields.filter_tags     || [],
    filter_ai_only:  !!fields.filter_ai_only,
    updated_at:      new Date().toISOString(),
  };
  const { data, error } = await db
    .from('library_saved_searches')
    .upsert(payload)
    .select()
    .single();
  if (error) { console.error('saveSavedSearch:', error.message); return { error: error.message }; }
  return { row: data };
};

export const deleteSavedSearch = async (id) => {
  const { error } = await db.from('library_saved_searches').delete().eq('id', id);
  if (error) { console.error('deleteSavedSearch:', error.message); return false; }
  return true;
};

// ── Library L4: cloud connectors (Google Drive, Dropbox, etc.) ───

export const CLOUD_PROVIDERS = [
  { id: 'google_drive', label: 'Google Drive', icon: '📁' },
  { id: 'dropbox',      label: 'Dropbox',      icon: '📦' },
  { id: 'onedrive',     label: 'OneDrive',     icon: '☁️' },
  { id: 'box',          label: 'Box',          icon: '📂' },
  { id: 's3',           label: 'S3 (compatible)', icon: '🪣' },
];

export const loadCloudConnections = async (orgId) => {
  const { data, error } = await db
    .from('library_cloud_connections')
    .select('id, provider, connection_name, external_folder_id, external_folder_name, sync_mode, auto_import_metadata, last_synced_at, last_sync_error, active, created_at')
    // NOTE: never select auth_blob from the client. Server-only.
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });
  if (error) { console.error('loadCloudConnections:', error.message); return []; }
  return data || [];
};

export const saveCloudConnection = async (orgId, userId, fields) => {
  // For now the auth_blob is a placeholder — real OAuth requires
  // server-side flow which is queued for follow-up. Admins can
  // save a connection stub now and wire creds later.
  const payload = {
    ...(fields.id ? { id: fields.id } : {}),
    org_id:                orgId,
    provider:              fields.provider,
    connection_name:       fields.connection_name?.trim() || 'Untitled connection',
    external_folder_id:    fields.external_folder_id?.trim() || null,
    external_folder_name:  fields.external_folder_name?.trim() || null,
    auth_type:             fields.auth_type || 'oauth',
    sync_mode:             fields.sync_mode || 'pull_on_demand',
    auto_import_metadata:  fields.auto_import_metadata !== false,
    active:                fields.active !== false,
    created_by_user_id:    userId,
    updated_at:            new Date().toISOString(),
  };
  const { data, error } = await db
    .from('library_cloud_connections')
    .upsert(payload)
    .select()
    .single();
  if (error) { console.error('saveCloudConnection:', error.message); return { error: error.message }; }
  return { row: data };
};

export const deleteCloudConnection = async (id) => {
  const { error } = await db.from('library_cloud_connections').delete().eq('id', id);
  if (error) { console.error('deleteCloudConnection:', error.message); return false; }
  return true;
};

// ── Library L3: per-doc + per-search ACL ─────────────────────────

export const LIBRARY_PERMISSIONS = [
  { id: 'view',         label: 'View metadata only' },
  { id: 'download',     label: 'Can download the file' },
  { id: 'ai_reference', label: 'AI can reference (their drafts see it)' },
];

// Load all ACL grants for a single library doc.
export const loadLibraryDocAccess = async (docId) => {
  const { data, error } = await db
    .from('proposal_library_access')
    .select('*')
    .eq('doc_id', docId)
    .order('created_at', { ascending: true });
  if (error) { console.error('loadLibraryDocAccess:', error.message); return []; }
  return data || [];
};

// Grant access to a single doc. `principal` is one of:
//   { type: 'user', id: UUID }
//   { type: 'org',  id: UUID }
//   { type: 'company', name: string }
export const grantLibraryDocAccess = async (docId, principal, permission, grantedByUserId) => {
  const payload = {
    doc_id:            docId,
    principal_type:    principal.type,
    principal_id:      principal.type === 'company' ? null : (principal.id || null),
    principal_name:    principal.type === 'company' ? (principal.name || null) : null,
    permission:        permission || 'view',
    granted_by_user_id: grantedByUserId || null,
  };
  const { data, error } = await db
    .from('proposal_library_access')
    .insert(payload)
    .select()
    .single();
  if (error) {
    // Unique violation = already granted — treat as success (idempotent)
    if (error.code === '23505') return { row: null, existed: true };
    console.error('grantLibraryDocAccess:', error.message);
    return { error: error.message };
  }
  return { row: data };
};

export const revokeLibraryDocAccess = async (aclRowId) => {
  const { error } = await db
    .from('proposal_library_access')
    .delete()
    .eq('id', aclRowId);
  if (error) { console.error('revokeLibraryDocAccess:', error.message); return false; }
  return true;
};

// Bulk grant: apply the SAME (principal, permission) to N docs at
// once. Used by the search-and-select-all flow — e.g. "grant reviewer
// X ai_reference on every NOAA-tagged doc". Returns { ok, existed,
// failed } counters.
export const bulkGrantLibraryAccess = async (docIds, principal, permission, grantedByUserId) => {
  if (!Array.isArray(docIds) || docIds.length === 0) return { ok: 0, existed: 0, failed: 0 };
  let ok = 0, existed = 0, failed = 0;
  for (const id of docIds) {
    const r = await grantLibraryDocAccess(id, principal, permission, grantedByUserId);
    if (r.error) failed++;
    else if (r.existed) existed++;
    else ok++;
  }
  return { ok, existed, failed };
};

// List users in the org that can be picked as grant targets. Excludes
// self. Used by the ACL editor picker.
export const loadOrgUsersForAcl = async (orgId) => {
  const { data, error } = await db
    .from('users')
    .select('id, name, email, role, company')
    .eq('org_id', orgId)
    .order('name');
  if (error) { console.error('loadOrgUsersForAcl:', error.message); return []; }
  return data || [];
};

// Bulk-patch multiple library docs. Used by the Library L2 bulk-ops
// UI (bulk retag / recategorize a search-filtered set). Runs updates
// sequentially so callers can display a progress indicator; returns
// { ok, failed } counts.
export const bulkPatchLibrary = async (docIds, patch) => {
  if (!Array.isArray(docIds) || docIds.length === 0) return { ok: 0, failed: 0 };
  let ok = 0, failed = 0;
  for (const id of docIds) {
    const { error } = await db.from('proposal_library').update(patch).eq('id', id);
    if (error) failed++; else ok++;
  }
  return { ok, failed };
};

// Bulk-merge tags/sectors/clients on multiple library docs. Unlike
// bulkPatchLibrary (raw overwrite), this reads each doc first,
// merges the arrays with existing values, and writes back — so the
// caller doesn't clobber pre-existing tags.
export const bulkMergeLibraryTags = async (docs, { addSectors, addClients, addTags, setDocType }) => {
  let ok = 0, failed = 0;
  for (const d of docs) {
    const patch = {};
    if (addSectors?.length) patch.sectors = Array.from(new Set([...(d.sectors || []), ...addSectors]));
    if (addClients?.length) patch.clients = Array.from(new Set([...(d.clients || []), ...addClients]));
    if (addTags?.length)    patch.tags    = Array.from(new Set([...(d.tags    || []), ...addTags]));
    if (setDocType)         patch.document_type = setDocType;
    if (Object.keys(patch).length === 0) continue;
    const { error } = await db.from('proposal_library').update(patch).eq('id', d.id);
    if (error) failed++; else ok++;
  }
  return { ok, failed };
};

// Roll up token spend for a single opportunity. Returns total cost +
// token count + per-feature breakdown. Used by the cost chip in the
// Proposal Workspace so users can see "this proposal cost $0.52 to
// draft so far" without leaving the opportunity view.
export const loadOpportunityTokenUsage = async (opportunityId) => {
  if (!opportunityId) return { total_cost: 0, total_tokens: 0, count: 0, byFeature: [] };
  const { data, error } = await db
    .from('token_usage_log')
    .select('feature, model, input_tokens, output_tokens, total_tokens, cost_usd, created_at')
    .eq('opportunity_id', opportunityId);
  if (error) { console.error('loadOpportunityTokenUsage:', error.message); return { total_cost: 0, total_tokens: 0, count: 0, byFeature: [] }; }
  const rows = data || [];
  let total_cost = 0;
  let total_tokens = 0;
  const byFeature = {};
  for (const r of rows) {
    const cost = Number(r.cost_usd) || 0;
    const toks = Number(r.total_tokens) || 0;
    total_cost   += cost;
    total_tokens += toks;
    const f = r.feature || 'unknown';
    if (!byFeature[f]) byFeature[f] = { feature: f, calls: 0, cost: 0, tokens: 0 };
    byFeature[f].calls  += 1;
    byFeature[f].cost   += cost;
    byFeature[f].tokens += toks;
  }
  return {
    total_cost,
    total_tokens,
    count: rows.length,
    byFeature: Object.values(byFeature).sort((a, b) => b.cost - a.cost),
  };
};

// Lightweight opportunity fetch — used when we need just the name +
// client for display (e.g. showing the currently-referenced
// opportunity's name in the UI panel).
export const loadOpportunityBrief = async (id) => {
  if (!id) return null;
  const { data } = await db
    .from('opportunities')
    .select('id, name, client_name, stage')
    .eq('id', id)
    .maybeSingle();
  return data || null;
};

// Set / clear the persistent AI reference for an opportunity. When
// set, every drafter call reads the referenced opportunity's sections
// as "match this style" context.
export const setOpportunityReference = async (oppId, referenceOppId) => {
  const { error } = await db
    .from('opportunities')
    .update({
      reference_opportunity_id: referenceOppId || null,
      updated_at:               new Date().toISOString(),
    })
    .eq('id', oppId);
  if (error) return { error: error.message };
  return { ok: true };
};

// One-call combo: apply the flagged subset of a source opportunity's
// settings to the target. `apply` is an object of booleans:
//   { theme, directives, reference }
export const referencePastProposal = async (fromOppId, toOppId, apply, sourceLabel, userId) => {
  const results = { theme: null, directives: null, reference: null };
  if (apply.theme) {
    results.theme = await cloneOpportunityTheme(fromOppId, toOppId);
  }
  if (apply.directives) {
    results.directives = await cloneOpportunityDirectives(fromOppId, toOppId, sourceLabel, userId);
  }
  if (apply.reference) {
    results.reference = await setOpportunityReference(toOppId, fromOppId);
  }
  const errs = Object.values(results).filter(r => r?.error).map(r => r.error);
  return errs.length ? { error: errs.join('; ') } : { ok: true, ...results };
};

// ── Per-opportunity proposal theme (logo, color, font) ────────────
// Used by the PDF + preview renderer to brand each proposal to the
// buyer's expectations instead of always shipping 40Geo's look.

export const FONT_CHOICES = [
  { id: 'sans',       label: 'Sans-serif (default)', css: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' },
  { id: 'montserrat', label: 'Montserrat',           css: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  { id: 'inter',      label: 'Inter',                css: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  { id: 'serif',      label: 'Serif',                css: 'Georgia, Cambria, "Times New Roman", Times, serif' },
];

export const HEADER_STYLES = [
  { id: 'default',  label: 'Default — logo + bars header' },
  { id: 'minimal',  label: 'Minimal — text only, single rule' },
  { id: 'centered', label: 'Centered — logo + title centered' },
];

export const TEMPLATE_STYLES = [
  { id: 'standard',    label: 'Standard — meta grid + TOC + numbered sections (40Geo default)' },
  { id: 'cover_first', label: 'Cover-first — large cover image + Prepared for/by block (Oxy-style)' },
  { id: 'minimal',     label: 'Minimal — title + sections only, no boxes or TOC' },
];

export const DEFAULT_THEME = {
  primary_color:            '#E87722',
  font_family:              'sans',
  header_style:             'default',
  template_style:           'standard',
  logo_storage_path:        null,
  cover_image_storage_path: null,
  company_name:             null,
  prepared_for:             null,        // text block — e.g. client name + attn + address
  prepared_by:              null,        // text block — your side
  subtitle:                 null,        // small text shown under the proposal title
  show_page_numbers:        true,        // print "Page X of Y" on every page
  footer_text:              null,        // optional confidentiality / footer line printed on every page
};

export const resolveTheme = (raw) => {
  const t = raw && typeof raw === 'object' ? raw : {};
  return {
    primary_color:            t.primary_color || DEFAULT_THEME.primary_color,
    font_family:              t.font_family   || DEFAULT_THEME.font_family,
    header_style:             t.header_style  || DEFAULT_THEME.header_style,
    template_style:           t.template_style || DEFAULT_THEME.template_style,
    logo_storage_path:        t.logo_storage_path || null,
    cover_image_storage_path: t.cover_image_storage_path || null,
    company_name:             t.company_name || null,
    prepared_for:             t.prepared_for || null,
    prepared_by:              t.prepared_by  || null,
    subtitle:                 t.subtitle     || null,
    show_page_numbers:        t.show_page_numbers !== false,
    footer_text:              t.footer_text  || null,
  };
};

// Upload an image (logo or cover) into the proposal-library bucket.
// Returns the storage_path on success — caller writes it to the
// theme JSONB on the opportunity.
export const uploadProposalThemeImage = async (orgId, opportunityId, kind, file) => {
  if (!file) return { error: 'no file' };
  if (!file.type?.startsWith('image/')) return { error: 'Only image files (PNG, JPG, WEBP) are accepted.' };
  if (file.size > 5 * 1024 * 1024)      return { error: 'Image is larger than 5MB.' };
  const ext  = (file.name?.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
  const id   = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
  const path = `${orgId}/${opportunityId}/theme/${kind}-${id}.${ext}`;
  const { error: upErr } = await db.storage
    .from('proposal-library')
    .upload(path, file, { contentType: file.type, upsert: false });
  if (upErr) { console.error('uploadProposalThemeImage:', upErr.message); return { error: upErr.message }; }
  return { storage_path: path };
};

// ── Workspace AI directives (cross-cutting style instructions) ────
// Free-form persistent instructions the team gives the AI for the
// whole proposal. Every drafter call (per-section, full-proposal,
// chat) reads these and surfaces them as the FIRST priority block
// in the system prompt so they take precedence over the AI's own
// defaults.

// Path B — our generic /api/db/query endpoint doesn't support PostgREST's
// embedded-select syntax (`select('*, users:col(name, email)')`).  Split
// into two queries: fetch directives, then batch-load their authors.
// Result shape matches what the caller expects.
async function hydrateWithUsers(rows, userIdKey) {
  if (!rows?.length) return rows;
  const ids = Array.from(new Set(rows.map(r => r?.[userIdKey]).filter(Boolean)));
  if (!ids.length) return rows;
  const { data: users } = await db
    .from('users').select('id, name, email').in('id', ids);
  const byId = new Map((users || []).map(u => [u.id, u]));
  return rows.map(r => ({ ...r, users: byId.get(r?.[userIdKey]) || null }));
}

export const loadOpportunityAiDirectives = async (opportunityId) => {
  const { data, error } = await db
    .from('opportunity_ai_directives')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('created_at', { ascending: true });
  if (error) { console.error('loadOpportunityAiDirectives:', error.message); return []; }
  return hydrateWithUsers(data || [], 'created_by_user_id');
};

export const addOpportunityAiDirective = async (opportunityId, content, userId) => {
  if (!content?.trim()) return { error: 'empty directive' };
  const { data, error } = await db
    .from('opportunity_ai_directives')
    .insert({
      opportunity_id:     opportunityId,
      content:            content.trim(),
      created_by_user_id: userId,
    })
    .select('*')
    .single();
  if (error) { console.error('addOpportunityAiDirective:', error.message); return { error: error.message }; }
  const hydrated = (await hydrateWithUsers([data], 'created_by_user_id'))[0];
  return { row: hydrated };
};

export const deleteOpportunityAiDirective = async (id) => {
  const { error } = await db.from('opportunity_ai_directives').delete().eq('id', id);
  if (error) { console.error('deleteOpportunityAiDirective:', error.message); return false; }
  return true;
};

// Phase 5B: ONE-SHOT full-proposal drafter — Coach writes the entire
// proposal coherently in one pass. Mirrors the "paste past proposal
// + notes into Claude, get polished proposal back" workflow.
export const draftFullProposal = async (opportunityId, opts = {}) => {
  const { mode = 'fill_empty', linkedContext, libraryScope, model } = opts;
  const { data: { session } } = await db.auth.getSession();
  if (!session?.access_token) return { error: 'Not authenticated' };
  try {
    const res = await fetch('/api/opportunity-draft-full-proposal', {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        opportunity_id: opportunityId,
        mode,
        linked_context: linkedContext || null,
        library_scope:  libraryScope  || null,
        model:          model         || undefined,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { error: body.error || `HTTP ${res.status}` };
    return body;
  } catch (e) {
    return { error: e.message || 'network error' };
  }
};

// Phase 4B: AI section drafter — Coach writes an initial draft for
// a single section. Result lands in proposal_sections.ai_generated_content
// (the editor surfaces it as "Load AI draft" — the contributor's own
// content field is not overwritten until they accept the draft).
export const draftProposalSection = async (sectionId, opts = {}) => {
  const { instructions, linkedContext, libraryScope, model } = opts;
  const { data: { session } } = await db.auth.getSession();
  if (!session?.access_token) return { error: 'Not authenticated' };
  try {
    const res = await fetch('/api/opportunity-section-draft', {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        section_id: sectionId,
        instructions:    instructions || '',
        linked_context:  linkedContext || null,
        library_scope:   libraryScope  || null,
        model:           model         || undefined,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { error: body.error || `HTTP ${res.status}` };
    return { draft: body.draft, usage: body.usage, model: body.model };
  } catch (e) {
    return { error: e.message || 'network error' };
  }
};

export const submitProposalSignoff = async (sectionId, userId, fields) => {
  const payload = {
    section_id:    sectionId,
    user_id:       userId,
    capacity_used: fields.capacity_used || 'contributor',
    decision:      fields.decision,
    comments:      fields.comments?.trim() || null,
  };
  const { data, error } = await db
    .from('proposal_signoffs')
    .insert(payload)
    .select()
    .single();
  if (error) { console.error('submitProposalSignoff:', error.message); return { error: error.message }; }
  return { row: data };
};

// ── Opportunity AI Coach (threads + messages) ─────────────────
// Mirrors the deal_ai_* shape: per-user threads on each opportunity,
// per-user RLS, server-side auto-titling on first message.
export const loadOpportunityAiThreads = async (opportunityId) => {
  const { data, error } = await db
    .from('opportunity_ai_threads')
    .select('*')
    .eq('opportunity_id', opportunityId)
    .order('updated_at', { ascending: false });
  if (error) { console.error('loadOpportunityAiThreads:', error.message); return []; }
  return data || [];
};

export const createOpportunityAiThread = async (opportunityId, userId, title = 'New conversation') => {
  const { data, error } = await db
    .from('opportunity_ai_threads')
    .insert({ opportunity_id: opportunityId, user_id: userId, title })
    .select()
    .single();
  if (error) { console.error('createOpportunityAiThread:', error.message); return null; }
  return data;
};

export const renameOpportunityAiThread = async (threadId, title) => {
  const { error } = await db
    .from('opportunity_ai_threads')
    .update({ title, updated_at: new Date().toISOString() })
    .eq('id', threadId);
  if (error) { console.error('renameOpportunityAiThread:', error.message); return false; }
  return true;
};

export const deleteOpportunityAiThread = async (threadId) => {
  const { error } = await db.from('opportunity_ai_threads').delete().eq('id', threadId);
  if (error) { console.error('deleteOpportunityAiThread:', error.message); return false; }
  return true;
};

export const loadOpportunityAiMessages = async (opportunityId, threadId = null) => {
  let q = db
    .from('opportunity_ai_messages')
    .select('id, thread_id, role, content, model, input_tokens, output_tokens, created_at')
    .eq('opportunity_id', opportunityId)
    .order('created_at', { ascending: true });
  if (threadId) q = q.eq('thread_id', threadId);
  const { data, error } = await q;
  if (error) { console.error('loadOpportunityAiMessages:', error.message); return []; }
  return data || [];
};

export const sendOpportunityAiMessage = async ({ opportunityId, message, model = null, threadId = null, linkedContext = null, libraryScope = null }) => {
  const { data: { session } } = await db.auth.getSession();
  const token = session?.access_token;
  if (!token) return { error: 'not signed in' };
  const resp = await fetch('/api/opportunity-ai-chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      opportunity_id: opportunityId,
      message,
      model,
      thread_id: threadId,
      // Notes module + CRM activities live in localStorage; the
      // client passes the relevant slice in the body so the server
      // can ground its reply without needing those records in the DB.
      ...(linkedContext ? { linked_context: linkedContext } : {}),
      // Library scope: filter set (sectors / clients / doc_types).
      // Server loads matching AI-prep'd library docs and attaches
      // them to the conversation. enabled=false skips library entirely.
      ...(libraryScope ? { library_scope: libraryScope } : {}),
    }),
  });
  let body = {};
  try { body = await resp.json(); } catch {}
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body; // { reply, model, usage, thread_id, library_attached }
};

// Share a deal-AI thread with another user. Server clones the thread
// + messages into the recipient's view (per-user RLS, so we
// physically copy rather than share a row).
export const shareDealAiThread = async ({ threadId, recipientUserId, note = null }) => {
  const { data: { session } } = await db.auth.getSession();
  const token = session?.access_token;
  if (!token) return { error: 'not signed in' };
  const resp = await fetch('/api/share-deal-ai-thread', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ thread_id: threadId, recipient_user_id: recipientUserId, note }),
  });
  let body = {};
  try { body = await resp.json(); } catch {}
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body;
};

// ── ORG SETTINGS ──────────────────────────────────────────────────

export const loadOrgSettings = async (orgId) => {
  // maybeSingle: orgs without a row in org_settings (brand-new orgs,
  // or after a manual delete) returned 406 with .single(). The app
  // treats null as "use defaults" so a missing row should be silent.
  const { data } = await db
    .from("org_settings")
    .select("*")
    .eq("org_id", orgId)
    .maybeSingle();
  return data ?? null;
};

export const saveOrgSettings = async (orgId, settings) => {
  const { error } = await db
    .from("org_settings")
    .upsert({ ...settings, org_id: orgId, updated_at: new Date().toISOString() }, { onConflict: "org_id" });
  if (error) console.error("saveOrgSettings:", error.message);
  return !error;
};

// Targeted save — only writes the ai_models JSONB column
export const saveAiModels = async (orgId, aiModels) => {
  const { error } = await db
    .from("org_settings")
    .upsert({ org_id: orgId, ai_models: aiModels, updated_at: new Date().toISOString() }, { onConflict: "org_id" });
  if (error) console.error("saveAiModels:", error.message);
  return !error;
};

export const uploadOrgLogo = async (orgId, file) => {
  const ext  = file.name.split(".").pop();
  const path = `${orgId}/logo.${ext}`;
  const { data, error } = await db.storage
    .from("org-logos")
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) { console.error("uploadOrgLogo:", error.message); return null; }
  const { data: { publicUrl } } = db.storage.from("org-logos").getPublicUrl(data.path);
  return publicUrl;
};

export const uploadMessageAttachment = async (orgId, channelId, file) => {
  const path = `${orgId}/${channelId}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const { data, error } = await db.storage
    .from('chat-attachments')
    .upload(path, file, { contentType: file.type });
  if (error) { console.error('uploadMessageAttachment:', error.message); return null; }
  const { data: { publicUrl } } = db.storage.from('chat-attachments').getPublicUrl(data.path);
  return { url: publicUrl, name: file.name, type: file.type, size: file.size };
};

export const saveUserRole = async (userId, role) => {
  const { error } = await db
    .from("users")
    .update({ role })
    .eq("id", userId);
  if (error) console.error("saveUserRole:", error.message);
  return !error;
};

export const saveUserFeatureOverrides = async (userId, overrides) => {
  const { error } = await db
    .from("users")
    .update({ feature_overrides: overrides })
    .eq("id", userId);
  if (error) console.error("saveUserFeatureOverrides:", error.message);
  return !error;
};

export const saveUserContact = async (userId, { phone, email, name }) => {
  const update = {};
  if (phone !== undefined) update.phone = phone;
  if (email !== undefined) update.email = email;
  if (name  !== undefined) update.name  = name;
  const { error } = await db.from("users").update(update).eq("id", userId);
  if (error) console.error("saveUserContact:", error.message);
  return !error;
};

export const setUserActive = async (userId, isActive) => {
  const { error } = await db.from("users").update({ is_active: isActive }).eq("id", userId);
  if (error) console.error("setUserActive:", error.message);
  return !error;
};

// Assign a user to one of the companies in public.companies. Passing null
// (or '') clears the assignment. Used by Admin → Users to put APEM staff
// inside the "APEM Group" company so their feature overrides + deal room
// access kick in.
export const setUserCompany = async (userId, companyName) => {
  const value = companyName?.trim() || null;
  const { error } = await db.from("users").update({ company: value }).eq("id", userId);
  if (error) console.error("setUserCompany:", error.message);
  return !error;
};

export const addPreRegisteredUser = async (orgId, { name, email, role, phone }) => {
  const { data, error } = await db
    .from("users")
    .insert({
      org_id:    orgId,
      email:     email.trim().toLowerCase(),
      name:      name.trim(),
      role:      role || "viewer",
      phone:     phone || "",
      user_type: "internal",
      is_active: true,
    })
    .select("id, email, name, role, user_type, avatar_url, phone, feature_overrides")
    .single();
  if (error) { console.error("addPreRegisteredUser:", error.message); return null; }
  return {
    id:              data.id,
    name:            data.name || data.email.split("@")[0],
    email:           data.email,
    role:            data.role,
    userType:        data.user_type,
    phone:           data.phone || "",
    picture:         data.avatar_url,
    initials:        (data.name || data.email).split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2),
    accent:          "#E87722",
    status:          "offline",
    featureOverrides: data.feature_overrides || {},
  };
};

// ── TOKEN USAGE LOGGING ───────────────────────────────────────────
// Pricing per 1M tokens (update as models change)
const TOKEN_RATES = {
  'claude-sonnet-4-6':          { input: 3.00,  output: 15.00 },
  'claude-opus-4-6':            { input: 15.00, output: 75.00 },
  'claude-haiku-4-5-20251001':  { input: 0.80,  output: 4.00  },
  'gpt-4o':                     { input: 2.50,  output: 10.00 },
  'gpt-4o-mini':                { input: 0.15,  output: 0.60  },
  'gemini-1.5-flash':           { input: 0.075, output: 0.30  },
  'gemini-1.5-pro':             { input: 1.25,  output: 5.00  },
  'llama-3.1-local':            { input: 0,     output: 0     },
};

export const logTokenUsage = async ({ orgId, companyName, userId, userName, feature, model, inputTokens, outputTokens }) => {
  const rates = TOKEN_RATES[model] || TOKEN_RATES['claude-sonnet-4-6'];
  const costUsd = ((inputTokens * rates.input) + (outputTokens * rates.output)) / 1_000_000;
  const { error } = await db.from('token_usage_log').insert({
    org_id:       orgId        || null,
    company_name: companyName  || null,
    user_id:      userId       || null,
    user_name:    userName     || null,
    feature:      feature      || 'unknown',
    model:        model        || 'claude-sonnet-4-6',
    input_tokens:  inputTokens  || 0,
    output_tokens: outputTokens || 0,
    total_tokens:  (inputTokens || 0) + (outputTokens || 0),
    cost_usd:      costUsd,
  });
  if (error) console.warn('[token_usage_log]', error.message);
};

export const loadTokenUsage = async (orgId, limit = 500) => {
  const { data } = await db
    .from('token_usage_log')
    .select('*')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false })
    .limit(limit);
  return data || [];
};

// ── LAUNCHPADS (video huddles) ────────────────────────────────────

export const startLaunchpad = async (orgId, channelId, userId) => {
  // End any existing active launchpad for this channel
  await db
    .from("launchpads")
    .update({ ended_at: new Date().toISOString() })
    .eq("channel_id", channelId)
    .is("ended_at", null);

  // Generate a Jitsi room URL — free, no API key needed
  const roomName = `nuggets-${channelId.slice(0, 8)}-${Date.now()}`;
  const roomUrl  = `https://meet.jit.si/${roomName}`;

  const { data: launchpad, error } = await db
    .from("launchpads")
    .insert({ org_id: orgId, channel_id: channelId, room_url: roomUrl, room_name: roomName, created_by: userId })
    .select()
    .single();

  if (error) { console.error("startLaunchpad:", error.message); return { success: false, error: error.message }; }
  return { success: true, data: { launchpad } };
};

export const endLaunchpad = async (launchpadId) => {
  const { error } = await db
    .from("launchpads")
    .update({ ended_at: new Date().toISOString() })
    .eq("id", launchpadId);
  if (error) console.error("endLaunchpad:", error.message);
  return !error;
};

export const loadActiveLaunchpad = async (channelId) => {
  const { data } = await db
    .from("launchpads")
    .select("*")
    .eq("channel_id", channelId)
    .is("ended_at", null)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data ?? null;
};

export const subscribeToLaunchpads = (channelId, callback) =>
  db
    .channel(`launchpads-${channelId}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "launchpads", filter: `channel_id=eq.${channelId}` }, callback)
    .subscribe();

// ── MESSENGER ─────────────────────────────────────────────────────

// Return the user_ids of everyone in a channel (including sender).
// Used by push-triggering code that needs to notify all recipients.
// Calls a SECURITY DEFINER RPC (channel_member_ids) instead of a direct
// SELECT — the latter intermittently 500'd under the channel_members
// see_peers RLS policy.
export const loadChannelMemberIds = async (channelId) => {
  const { data, error } = await db.rpc('channel_member_ids', { ch: channelId });
  if (error) { console.error('channel_member_ids rpc:', error.message); return []; }
  return (data || []).map(r => r.user_id);
};

export const loadChannels = async (orgId, userId) => {
  // Step 1: public channels + member channel IDs (two separate queries — avoids
  // PostgREST nested join silently dropping rows when RLS blocks the join)
  const [{ data: publicChs }, { data: memberships }] = await Promise.all([
    db.from("channels").select("*").eq("org_id", orgId).eq("type", "public").order("name"),
    db.from("channel_members").select("channel_id").eq("user_id", userId),
  ]);

  const memberChannelIds = (memberships ?? []).map(m => m.channel_id).filter(Boolean);

  // Step 2: fetch full channel details for member channels
  let memberChannels = [];
  if (memberChannelIds.length > 0) {
    const { data: chs } = await db
      .from("channels")
      .select("*")
      .in("id", memberChannelIds)
      .eq("org_id", orgId);
    memberChannels = chs ?? [];
  }

  // Step 3: resolve otherUserId for DM channels
  // Requires RLS to allow reading other members of channels you belong to.
  // See db/migrations/20260403_channel_members_rls.sql if this returns empty.
  const dmChannels = memberChannels.filter(c => c.type === "dm");
  if (dmChannels.length > 0) {
    const { data: otherMembers } = await db
      .from("channel_members")
      .select("channel_id, user_id")
      .in("channel_id", dmChannels.map(c => c.id))
      .neq("user_id", userId);
    const map = Object.fromEntries((otherMembers ?? []).map(m => [m.channel_id, m.user_id]));
    dmChannels.forEach(c => {
      // Primary: from channel_members query
      // Fallback: created_by if the other person started the DM (covers 50% of cases when RLS is restrictive)
      c.otherUserId = map[c.id]
        || (c.created_by && c.created_by !== userId ? c.created_by : null)
        || null;
    });
  }

  const all = [...(publicChs ?? []), ...memberChannels];
  const seen = new Set();
  return all.filter(c => { if (!c || seen.has(c.id)) return false; seen.add(c.id); return true; });
};

export const createChannel = async (orgId, userId, name, description = "") => {
  const { data, error } = await db
    .from("channels")
    .insert({ org_id: orgId, name: name.toLowerCase().replace(/\s+/g, "-"), type: "public", created_by: userId, description })
    .select().single();
  if (error) console.error("createChannel:", error.message);
  return data ?? null;
};

export const getOrCreateDM = async (orgId, userId1, userId2) => {
  // Find an existing DM via a SECURITY DEFINER SQL function so the lookup
  // bypasses RLS — see migration 20260425_find_dm_channel.sql. The
  // previous client-side two-step lookup hit RLS edge cases that returned
  // empty even when an existing DM was present, causing duplicate DMs to
  // be created on every click.
  const { data: existingId, error: rpcErr } = await db
    .rpc("find_dm_channel", { org: orgId, u1: userId1, u2: userId2 });

  if (rpcErr) {
    console.error("find_dm_channel rpc:", rpcErr.message);
    // Fall through to create a new one — better than an outright failure.
  } else if (existingId) {
    const { data: dmCh } = await db
      .from("channels")
      .select("*")
      .eq("id", existingId)
      .maybeSingle();
    if (dmCh) return { ...dmCh, otherUserId: userId2 };
  }

  // No existing DM — create one + insert both participants.
  const { data: ch, error } = await db
    .from("channels")
    .insert({ org_id: orgId, name: "dm", type: "dm", created_by: userId1 })
    .select().single();
  if (error || !ch) { console.error("getOrCreateDM:", error?.message); return null; }

  const { error: memErr } = await db
    .from("channel_members")
    .insert([
      { channel_id: ch.id, user_id: userId1 },
      { channel_id: ch.id, user_id: userId2 },
    ]);
  if (memErr) {
    console.error("getOrCreateDM members:", memErr.message);
    // The channel exists but is half-initialized; nuke it so the next call
    // doesn't compound the problem.
    await db.from("channels").delete().eq("id", ch.id);
    return null;
  }
  return { ...ch, otherUserId: userId2 };
};

/**
 * Group DM (multi-party direct message).  Creates a channel of
 * type='dm' with N members (creator + everyone in otherUserIds).
 * When a group DM already exists between exactly this set of users,
 * returns it instead of creating a duplicate.
 *
 * A DM's "identity" is its member set — the same three people making
 * a group DM twice should re-open the existing thread, matching
 * Slack's mpim behaviour.
 */
export const getOrCreateGroupDM = async (orgId, creatorId, otherUserIds) => {
  // De-dupe + drop the creator if it snuck into the list.
  const others = [...new Set(otherUserIds.filter(id => id && id !== creatorId))];
  if (others.length === 0) return null;
  // 1-on-1 → route to the fast RPC path.
  if (others.length === 1) return await getOrCreateDM(orgId, creatorId, others[0]);

  const wantSet = new Set([creatorId, ...others]);
  const wantSize = wantSet.size;

  // Look for an existing DM channel whose full member set matches
  // exactly.  We do this client-side because the RLS-safe SQL helper
  // only handles the 1-on-1 case.  Scan is bounded by "DM channels I
  // belong to", not "all channels in the org".
  const { data: myMemberships } = await db
    .from("channel_members")
    .select("channel_id")
    .eq("user_id", creatorId);
  const myChannelIds = (myMemberships || []).map(m => m.channel_id);
  if (myChannelIds.length) {
    const { data: myDms } = await db
      .from("channels")
      .select("id, type")
      .in("id", myChannelIds)
      .eq("org_id", orgId)
      .eq("type", "dm");
    const dmIds = (myDms || []).map(c => c.id);
    if (dmIds.length) {
      const { data: allMembers } = await db
        .from("channel_members")
        .select("channel_id, user_id")
        .in("channel_id", dmIds);
      const byChannel = new Map();
      for (const m of (allMembers || [])) {
        if (!byChannel.has(m.channel_id)) byChannel.set(m.channel_id, new Set());
        byChannel.get(m.channel_id).add(m.user_id);
      }
      for (const [channelId, memberSet] of byChannel) {
        if (memberSet.size !== wantSize) continue;
        let match = true;
        for (const id of wantSet) if (!memberSet.has(id)) { match = false; break; }
        if (match) {
          const { data: ch } = await db.from("channels").select("*").eq("id", channelId).maybeSingle();
          if (ch) return ch;
        }
      }
    }
  }

  // No match — create a new DM channel + insert every member.
  const { data: ch, error } = await db
    .from("channels")
    .insert({ org_id: orgId, name: "dm", type: "dm", created_by: creatorId })
    .select().single();
  if (error || !ch) { console.error("getOrCreateGroupDM:", error?.message); return null; }

  const rows = [creatorId, ...others].map(uid => ({ channel_id: ch.id, user_id: uid }));
  const { error: memErr } = await db.from("channel_members").insert(rows);
  if (memErr) {
    console.error("getOrCreateGroupDM members:", memErr.message);
    await db.from("channels").delete().eq("id", ch.id);
    return null;
  }
  return ch;
};

// Batch fetch of channel_members rows for a set of channel IDs.
// Returns Map<channel_id, Set<user_id>>.  Used by Messenger to
// resolve group-DM display names ("Keith, Evan, Miles") and to
// decide when a DM's "online" dot lights up (any non-self member).
export const loadChannelMembersMap = async (channelIds) => {
  if (!channelIds?.length) return new Map();
  const { data } = await db.from("channel_members")
    .select("channel_id, user_id")
    .in("channel_id", channelIds);
  const map = new Map();
  for (const row of (data || [])) {
    if (!map.has(row.channel_id)) map.set(row.channel_id, new Set());
    map.get(row.channel_id).add(row.user_id);
  }
  return map;
};

export const loadMessages = async (channelId, limit = 100) => {
  const { data, error } = await db
    .from("messages")
    .select("id, content, created_at, user_id")
    .eq("channel_id", channelId)
    .order("created_at", { ascending: true })
    .limit(limit);
  if (error) console.error("loadMessages:", error.message);
  return data ?? [];
};

export const sendMessage = async (orgId, channelId, userId, content) => {
  const { data, error } = await db
    .from("messages")
    .insert({ org_id: orgId, channel_id: channelId, user_id: userId, content })
    .select("id, content, created_at, user_id, channel_id")
    .single();
  if (error) console.error("sendMessage:", error.message);
  // Fire push notifications to every other channel member.  Kicks off
  // in the background so send() returns immediately — a failed push
  // shouldn't hold up the UI acknowledging the message was sent.
  if (data) notifyMessageRecipients({ channelId, senderId: userId, content }).catch(() => {});
  return data ?? null;
};

// Best-effort push dispatch — never throws.  Skips the sender.  Runs
// its own queries because the caller doesn't have the channel-member
// list to hand.  Titles differ for DMs vs channels:
//   DM:      "Miles Roden"           preview...
//   Channel: "Miles Roden in #ops"   preview...
async function notifyMessageRecipients({ channelId, senderId, content }) {
  try {
    const { data: members } = await db.from('channel_members')
      .select('user_id').eq('channel_id', channelId);
    const recipients = (members || [])
      .map(m => m.user_id)
      .filter(id => id && id !== senderId);
    if (!recipients.length) return;

    const [chRes, senderRes] = await Promise.all([
      db.from('channels').select('name, type').eq('id', channelId).maybeSingle(),
      db.from('users').select('name, email').eq('id', senderId).maybeSingle(),
    ]);
    const ch     = chRes?.data || {};
    const sender = senderRes?.data || {};
    const senderName = sender.name || (sender.email ? sender.email.split('@')[0] : 'Someone');
    const isDm       = ch.type === 'dm';
    const preview    = String(content || '').replace(/\n+/g, ' ').slice(0, 140);

    await api.post('/api/push-notify', {
      user_ids: recipients,
      title:    isDm ? senderName : `${senderName} in #${ch.name || 'channel'}`,
      body:     preview || (isDm ? 'sent a message' : 'posted a message'),
      // tag collapses a burst of messages in the same channel into
      // a single OS notification stack
      tag:      `nuggets-msg-${channelId}`,
      data:     { url: '/#slack', channelId },
    });
  } catch (err) {
    // Silent — the message was already delivered; a push miss is a
    // "should have notified" not a "should retry the whole send".
    console.warn('[notifyMessageRecipients] failed:', err?.message || err);
  }
}

export const subscribeToMessages = (channelId, onInsert) =>
  db
    .channel(`messages-${channelId}`)
    .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages", filter: `channel_id=eq.${channelId}` }, onInsert)
    .subscribe();

// Ephemeral typing broadcast — nothing is written to Postgres.  The
// server rebroadcasts each call via pg_notify so anyone SSE-subscribed
// to `typing:<channel_id>` hears about it.  Debounce this at the
// caller (composer keystrokes); one broadcast every ~2.5s is plenty.
export const broadcastTyping = (channelId, opts = {}) => {
  return api.post('/api/typing', { channel_id: channelId, stopped: !!opts.stopped })
    .catch(() => {}); // typing is best-effort — swallow errors
};

// Subscribes to the typing topic for a channel.  onEvent receives
// { user_id, user_name, at, stopped } as fired by /api/typing.
export const subscribeToTyping = (channelId, onEvent) => {
  const ch = realtime.channel(`typing:${channelId}`);
  ch.on('typing',  payload => onEvent({ ...payload, stopped: false }));
  ch.on('stopped', payload => onEvent({ ...payload, stopped: true  }));
  ch.subscribe();
  return { unsubscribe: () => ch.unsubscribe() };
};

export const markChannelRead = async (channelId, userId) => {
  await db
    .from("channel_members")
    .upsert({ channel_id: channelId, user_id: userId, last_read_at: new Date().toISOString() }, { onConflict: "channel_id,user_id" });
};

export const searchMessages = async (channelIds, query) => {
  if (!channelIds.length || !query.trim()) return [];
  const { data } = await db
    .from('messages')
    .select('id, content, created_at, user_id, channel_id')
    .in('channel_id', channelIds)
    .ilike('content', `%${query}%`)
    .order('created_at', { ascending: false })
    .limit(40);
  return data ?? [];
};

export const editMessage = async (messageId, newContent) => {
  const { error } = await db
    .from('messages')
    .update({ content: newContent })
    .eq('id', messageId);
  if (error) console.error('editMessage:', error.message);
  return !error;
};

export const deleteMessage = async (messageId) => {
  const { error } = await db
    .from('messages')
    .update({ content: '*(This message was deleted)*' })
    .eq('id', messageId);
  if (error) console.error('deleteMessage:', error.message);
  return !error;
};

// Each caller passes a unique tag so multiple subscribers (root-level
// DM toaster + Messenger unread tracker) get distinct realtime channels
// — same topic name silently no-ops the second subscriber.
export const subscribeToOrgMessages = (orgId, onInsert, tag = 'main') =>
  db
    .channel(`org-messages:${orgId}:${tag}`)
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `org_id=eq.${orgId}` }, onInsert)
    .subscribe();

export const subscribeToNuggets = (orgId, onChange) =>
  db
    .channel(`org-nuggets:${orgId}`)
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'nuggets', filter: `org_id=eq.${orgId}` }, (p) => onChange('INSERT', p.new))
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'nuggets', filter: `org_id=eq.${orgId}` }, (p) => onChange('UPDATE', p.new))
    .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'nuggets', filter: `org_id=eq.${orgId}` }, (p) => onChange('DELETE', p.old))
    .subscribe();

// Fires when any channel in this org is created / updated (or channel_members
// row is created — needed so DMs appear on both participants' devices).
// `onChange` receives ('channel_insert'|'channel_update'|'member_insert', row).
export const subscribeToChannelList = (orgId, userId, onChange) =>
  db
    .channel(`org-channels:${orgId}:${userId}`)
    .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'channels', filter: `org_id=eq.${orgId}` },
        (p) => onChange('channel_insert', p.new))
    .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'channels', filter: `org_id=eq.${orgId}` },
        (p) => onChange('channel_update', p.new))
    .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'channel_members', filter: `user_id=eq.${userId}` },
        (p) => onChange('member_insert', p.new))
    .subscribe();

// Fires when this user's channel_members row is updated (last_read_at
// changes on another device → badges rebalance here).
export const subscribeToMyChannelMembership = (userId, onChange) =>
  db
    .channel(`my-channel-members:${userId}`)
    .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'channel_members', filter: `user_id=eq.${userId}` },
        (p) => onChange(p.new))
    .subscribe();

// Load unread counts for every channel the user belongs to. Returns
// { [channel_id]: n } so the messenger can render badges without local
// guesswork. Uses one round-trip and lets Postgres do the counting.
export const loadUnreadCounts = async (userId) => {
  // Pull last_read_at per channel first.
  const { data: mems } = await db
    .from('channel_members')
    .select('channel_id, last_read_at')
    .eq('user_id', userId);
  if (!mems?.length) return {};

  // For each channel, count messages after last_read_at. Sequential but
  // bounded by the number of channels the user is in (small).
  const out = {};
  await Promise.all(mems.map(async (m) => {
    const since = m.last_read_at || '1970-01-01T00:00:00Z';
    const { count } = await db
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('channel_id', m.channel_id)
      .gt('created_at', since)
      .neq('user_id', userId); // don't count my own messages as unread
    out[m.channel_id] = count || 0;
  }));
  return out;
};

// ── NOTIFICATION LOG ──────────────────────────────────────────────

export const loadNotificationLog = async (orgId, limit = 50) => {
  const { data } = await db
    .from("notification_log")
    .select("*")
    .eq("org_id", orgId)
    .order("created_at", { ascending: false })
    .limit(limit);
  return data ?? [];
};

// ── WEB PUSH SUBSCRIPTIONS ────────────────────────────────────────

/**
 * Save (upsert) the browser push subscription for a user.
 * Called after the user grants notification permission.
 */
export const savePushSubscription = async (userId, subscription) => {
  const { error } = await db
    .from('push_subscriptions')
    .upsert(
      { user_id: userId, subscription: JSON.stringify(subscription), updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    );
  if (error) console.error('savePushSubscription:', error.message);
  return !error;
};

/**
 * Load the stored push subscription for a user.
 * Returns the parsed subscription object or null.
 */
export const loadPushSubscription = async (userId) => {
  const { data } = await db
    .from('push_subscriptions')
    .select('subscription')
    .eq('user_id', userId)
    .maybeSingle();
  if (!data?.subscription) return null;
  try { return JSON.parse(data.subscription); } catch { return null; }
};

/**
 * Delete the push subscription for a user (opt-out or expired).
 */
export const deletePushSubscription = async (userId) => {
  await db.from('push_subscriptions').delete().eq('user_id', userId);
};

// ── NOTES ─────────────────────────────────────────────────────────

export const loadNotes = async (userId) => {
  console.log('[loadNotes] querying for user_id=', userId);
  const { data, error } = await db
    .from('notes')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });
  if (error) { console.error('[loadNotes] ERROR:', error.message, error); return null; }
  console.log('[loadNotes] rows returned:', data?.length ?? 0);
  if (!Array.isArray(data)) {
    console.error('[loadNotes] non-array response:', data);
    return null;
  }
  return data.map(d => ({
    id:          d.id,
    title:       d.title,
    content:     d.content || '',
    category:    d.category || 'stream',
    crmLink:     d.crm_link || '',
    // Force array shape.  A stray save that wrote `{}` (empty object)
    // to a JSONB column will otherwise crash any `.filter` /`.map`
    // downstream — that's how the "Taxonomics Data" note broke the
    // entire Notes module on 2026-07-08.
    tags:        Array.isArray(d.tags)         ? d.tags         : [],
    miniNuggets: Array.isArray(d.mini_nuggets) ? d.mini_nuggets : [],
    createdAt:   d.created_at,
    updatedAt:   d.updated_at,
  }));
};

export const saveNote = async (note, orgId, userId) => {
  const { error } = await db
    .from('notes')
    .upsert({
      id:           note.id,
      org_id:       orgId,
      user_id:      userId,
      title:        note.title || 'Untitled',
      content:      note.content || '',
      category:     note.category || 'stream',
      crm_link:     note.crmLink || '',
      // Same coercion on the way out — an object here would land in
      // JSONB as `{}` and break every future read.
      tags:         Array.isArray(note.tags)        ? note.tags        : [],
      mini_nuggets: Array.isArray(note.miniNuggets) ? note.miniNuggets : [],
      updated_at:   new Date().toISOString(),
    }, { onConflict: 'id' });
  if (error) console.error('saveNote ERROR:', error.message);
  return !error;
};

export const deleteNote = async (id) => {
  const { error } = await db.from('notes').delete().eq('id', id);
  if (error) console.error('deleteNote:', error.message);
  return !error;
};

// ── CRM (deals / proposals / contracts) ──────────────────────────
// The CRM module used to persist its state to a per-browser
// localStorage key.  These helpers move the same three entities into
// the DB (see db/migrations/20260707_crm_persistent.sql).
// Shape conventions:
//   * The DB uses snake_case columns; the CRM UI has always used
//     camelCase in state.  These helpers translate on read + write.
//   * IDs on the client are `crypto.randomUUID()` — no more
//     `crm-<timestamp>` strings.  A one-shot migration in the CRMModule
//     bootstraps localStorage rows over to the DB (re-issuing UUIDs)
//     the first time a user with legacy state opens the CRM.

const dbDealToUi = (r) => (!r ? null : {
  id:           r.id,
  title:        r.title,
  company:      r.company || '',
  contact:      r.contact || '',
  contactEmail: r.contact_email || '',
  contactPhone: r.contact_phone || '',
  value:        Number(r.value) || 0,
  arr:          Number(r.arr)   || 0,
  currency:     r.currency || 'USD',
  stage:        r.stage,
  probability:  r.probability,
  // DATE columns can arrive as 'YYYY-MM-DD' or as an ISO timestamp
  // depending on the driver's type parser; keep the calendar day only.
  closeDate:    r.close_date ? String(r.close_date).slice(0, 10) : null,
  wonDate:      r.won_date   ? String(r.won_date).slice(0, 10)   : null,
  lostReason:   r.lost_reason || '',
  source:       r.source || '',
  ownerUserId:  r.owner_user_id || null,
  proposalId:   r.proposal_id || null,
  opportunityId:r.opportunity_id || null,
  activities:   Array.isArray(r.activities) ? r.activities : [],
  createdAt:    r.created_at,
  updatedAt:    r.updated_at,
  // Everything not explicitly mapped lives in meta so custom fields
  // from CRMNewDealModal survive round-trips.
  ...(r.meta && typeof r.meta === 'object' ? r.meta : {}),
});

const uiDealToDb = (d, orgId) => ({
  id:            d.id,
  org_id:        orgId,
  title:         d.title,
  company:       d.company || null,
  contact:       d.contact || null,
  contact_email: d.contactEmail || null,
  contact_phone: d.contactPhone || null,
  value:         Number(d.value) || 0,
  arr:           Number(d.arr)   || 0,
  currency:      d.currency || 'USD',
  stage:         d.stage || 'lead',
  probability:   Number.isFinite(d.probability) ? d.probability : 10,
  close_date:    d.closeDate || null,
  won_date:      d.wonDate   || null,
  lost_reason:   d.lostReason || null,
  source:        d.source || null,
  owner_user_id: d.ownerUserId || null,
  proposal_id:   d.proposalId || null,
  opportunity_id:d.opportunityId || null,
  // JSONB columns need explicit JSON.stringify — pg's node driver
  // serializes plain JS arrays as Postgres array literals (`{a,b}`)
  // and INSERTs them into a jsonb column fail with "invalid input
  // syntax for type json".  Stringifying forces the payload through
  // as text and Postgres coerces text → jsonb via the implicit cast.
  activities:    JSON.stringify(Array.isArray(d.activities) ? d.activities : []),
  // Anything we don't map above lands in meta so nothing gets lost.
  meta: JSON.stringify(Object.fromEntries(Object.entries(d).filter(([k]) => ![
    'id','title','company','contact','contactEmail','contactPhone',
    'value','arr','currency','stage','probability','closeDate','wonDate','lostReason',
    'source','ownerUserId','proposalId','opportunityId','activities',
    'createdAt','updatedAt',
  ].includes(k)))),
});

const dbProposalToUi = (r) => (!r ? null : {
  id:         r.id,
  dealId:     r.deal_id || null,
  title:      r.title,
  client:     r.client || '',
  status:     r.status,
  totalValue: Number(r.total_value) || 0,
  currency:   r.currency || 'USD',
  sentAt:     r.sent_at,
  sections:   Array.isArray(r.sections) ? r.sections : [],
  createdAt:  r.created_at,
  updatedAt:  r.updated_at,
});

const uiProposalToDb = (p, orgId) => ({
  id:          p.id,
  org_id:      orgId,
  deal_id:     p.dealId || null,
  title:       p.title,
  client:      p.client || null,
  status:      p.status || 'draft',
  total_value: Number(p.totalValue) || 0,
  currency:    p.currency || 'USD',
  sent_at:     p.sentAt || null,
  // Same JSONB serialization workaround as uiDealToDb.activities.
  sections:    JSON.stringify(Array.isArray(p.sections) ? p.sections : []),
});

const dbContractToUi = (r) => (!r ? null : {
  id:                r.id,
  dealId:            r.deal_id || null,
  proposalId:        r.proposal_id || null,
  title:             r.title,
  client:            r.client || '',
  contact:           r.contact || '',
  value:             Number(r.value) || 0,
  arr:               Number(r.arr)   || 0,
  currency:          r.currency || 'USD',
  startDate:         r.start_date,
  endDate:           r.end_date,
  renewalDate:       r.renewal_date,
  status:            r.status,
  healthScore:      r.health_score,
  supportTickets:   r.support_tickets,
  supportHoursUsed: Number(r.support_hours_used) || 0,
  notes:             r.notes || '',
  createdAt:         r.created_at,
  updatedAt:         r.updated_at,
});

const uiContractToDb = (c, orgId) => ({
  id:                  c.id,
  org_id:              orgId,
  deal_id:             c.dealId || null,
  proposal_id:         c.proposalId || null,
  title:               c.title,
  client:              c.client || null,
  contact:             c.contact || null,
  value:               Number(c.value) || 0,
  arr:                 Number(c.arr)   || 0,
  currency:            c.currency || 'USD',
  start_date:          c.startDate || null,
  end_date:            c.endDate || null,
  renewal_date:        c.renewalDate || null,
  status:              c.status || 'active',
  health_score:        c.healthScore ?? null,
  support_tickets:     c.supportTickets ?? 0,
  support_hours_used:  Number(c.supportHoursUsed) || 0,
  notes:               c.notes || null,
});

export const loadCrmDeals = async (orgId) => {
  const { data, error } = await db.from('crm_deals')
    .select('*').eq('org_id', orgId).order('updated_at', { ascending: false });
  if (error) { console.error('loadCrmDeals:', error.message); return null; }
  return (data || []).map(dbDealToUi);
};
export const upsertCrmDeal = async (deal, orgId) => {
  const { data, error } = await db.from('crm_deals')
    .upsert(uiDealToDb(deal, orgId), { onConflict: 'id' })
    .select().single();
  if (error) { console.error('upsertCrmDeal:', error.message); return { error: error.message }; }
  return { row: dbDealToUi(data) };
};
export const deleteCrmDeal = async (id) => {
  const { error } = await db.from('crm_deals').delete().eq('id', id);
  if (error) console.error('deleteCrmDeal:', error.message);
  return !error;
};

export const loadCrmProposals = async (orgId) => {
  const { data, error } = await db.from('crm_proposals')
    .select('*').eq('org_id', orgId).order('updated_at', { ascending: false });
  if (error) { console.error('loadCrmProposals:', error.message); return null; }
  return (data || []).map(dbProposalToUi);
};
export const upsertCrmProposal = async (proposal, orgId) => {
  const { data, error } = await db.from('crm_proposals')
    .upsert(uiProposalToDb(proposal, orgId), { onConflict: 'id' })
    .select().single();
  if (error) { console.error('upsertCrmProposal:', error.message); return { error: error.message }; }
  return { row: dbProposalToUi(data) };
};
export const deleteCrmProposal = async (id) => {
  const { error } = await db.from('crm_proposals').delete().eq('id', id);
  if (error) console.error('deleteCrmProposal:', error.message);
  return !error;
};

export const loadCrmContracts = async (orgId) => {
  const { data, error } = await db.from('crm_contracts')
    .select('*').eq('org_id', orgId).order('updated_at', { ascending: false });
  if (error) { console.error('loadCrmContracts:', error.message); return null; }
  return (data || []).map(dbContractToUi);
};
export const upsertCrmContract = async (contract, orgId) => {
  const { data, error } = await db.from('crm_contracts')
    .upsert(uiContractToDb(contract, orgId), { onConflict: 'id' })
    .select().single();
  if (error) { console.error('upsertCrmContract:', error.message); return { error: error.message }; }
  return { row: dbContractToUi(data) };
};
export const deleteCrmContract = async (id) => {
  const { error } = await db.from('crm_contracts').delete().eq('id', id);
  if (error) console.error('deleteCrmContract:', error.message);
  return !error;
};

// ── LAUNCHPAD INVITES ─────────────────────────────────────────────

/**
 * Broadcast a launchpad invite to specific users via the realtime layer.
 * Each invited user has their own channel: launchpad-invites:{userId}
 */
export const broadcastLaunchpadInvite = async ({ sessionId, channelId, channelName, fromUser, toUserIds }) => {
  await Promise.all(toUserIds.map(userId => {
    const ch = db.channel(`launchpad-invites:${userId}`);
    return ch.send({
      type:    'broadcast',
      event:   'incoming-call',
      payload: { sessionId, channelId, channelName, fromName: fromUser.name, fromId: fromUser.id },
    }).then(() => db.removeChannel(ch));
  }));
};

/**
 * Subscribe to incoming launchpad invites for the current user.
 * Returns the realtime channel so the caller can unsubscribe.
 */
export const subscribeLaunchpadInvites = (userId, callback) => {
  const channel = db
    .channel(`launchpad-invites:${userId}`)
    .on('broadcast', { event: 'incoming-call' }, ({ payload }) => callback(payload))
    .subscribe();
  return channel;
};

// ── DB HEALTH MONITOR ─────────────────────────────────────────────
// Helpers that back the Admin → DB Health tab. Most just wrap the DB
// queries for our new tables; discover/columns/run call the Vercel API
// routes directly (they need server-side Postgres access).

export const loadExternalDatabases = async (orgId) => {
  const { data, error } = await db
    .from('external_databases')
    .select('*')
    .eq('org_id', orgId)
    .order('name');
  if (error) { console.error('loadExternalDatabases:', error.message); return []; }
  return data || [];
};

export const saveExternalDatabase = async (row) => {
  const { error } = await db
    .from('external_databases')
    .upsert({ ...row, updated_at: new Date().toISOString() }, { onConflict: 'id' });
  if (error) console.error('saveExternalDatabase:', error.message);
  return !error;
};

export const loadMonitoredTables = async (orgId, databaseId = null) => {
  let q = db.from('monitored_tables').select('*').eq('org_id', orgId);
  if (databaseId) q = q.eq('database_id', databaseId);
  const { data, error } = await q.order('schema_name').order('table_name');
  if (error) { console.error('loadMonitoredTables:', error.message); return []; }
  return data || [];
};

export const saveMonitoredTable = async (row) => {
  const { error } = await db
    .from('monitored_tables')
    .upsert({ ...row, updated_at: new Date().toISOString() }, { onConflict: 'id' });
  if (error) console.error('saveMonitoredTable:', error.message);
  return !error;
};

export const bulkAddMonitoredTables = async (orgId, databaseId, tables) => {
  // tables: [{ schema_name, table_name }, …]  — called after the discover picker.
  const rows = tables.map(t => ({
    org_id: orgId,
    database_id: databaseId,
    schema_name: t.schema_name,
    table_name:  t.table_name,
  }));
  const { error } = await db
    .from('monitored_tables')
    .upsert(rows, { onConflict: 'database_id,schema_name,table_name', ignoreDuplicates: true });
  if (error) console.error('bulkAddMonitoredTables:', error.message);
  return !error;
};

export const deleteMonitoredTable = async (id) => {
  const { error } = await db.from('monitored_tables').delete().eq('id', id);
  if (error) console.error('deleteMonitoredTable:', error.message);
  return !error;
};

export const loadAppOwnership = async (orgId) => {
  const { data, error } = await db
    .from('app_ownership')
    .select('*')
    .eq('org_id', orgId);
  if (error) { console.error('loadAppOwnership:', error.message); return []; }
  return data || [];
};

export const saveAppOwnership = async (row) => {
  const { error } = await db
    .from('app_ownership')
    .upsert({ ...row, updated_at: new Date().toISOString() }, { onConflict: 'id' });
  if (error) console.error('saveAppOwnership:', error.message);
  return !error;
};

export const deleteAppOwnership = async (id) => {
  const { error } = await db.from('app_ownership').delete().eq('id', id);
  if (error) console.error('deleteAppOwnership:', error.message);
  return !error;
};

export const loadHealthLog = async (orgId, { limit = 100, monitoredTableId = null } = {}) => {
  let q = db.from('table_health_log').select('*').eq('org_id', orgId);
  if (monitoredTableId) q = q.eq('monitored_table_id', monitoredTableId);
  const { data, error } = await q.order('checked_at', { ascending: false }).limit(limit);
  if (error) { console.error('loadHealthLog:', error.message); return []; }
  return data || [];
};

// Wraps the Vercel API routes. These go through the user's session JWT
// (the browser DB client handles that for us).
const callMonitorApi = async (path, { method = 'GET' } = {}) => {
  const { data: { session } } = await db.auth.getSession();
  if (!session) return { error: 'not signed in' };
  const resp = await fetch(path, {
    method,
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body;
};

export const discoverTablesInDatabase = (databaseId) =>
  callMonitorApi(`/api/monitor/discover?database_id=${encodeURIComponent(databaseId)}`);

export const fetchColumnsForTable = (databaseId, schema, table) =>
  callMonitorApi(
    `/api/monitor/columns?database_id=${encodeURIComponent(databaseId)}` +
    `&schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}`
  );

export const runMonitorNow = () => callMonitorApi('/api/monitor/run', { method: 'POST' });

// ── CONTACTS (CRM address book) ───────────────────────────────────

export const loadContacts = async (orgId) => {
  const { data, error } = await db
    .from('contacts')
    .select('*')
    .eq('org_id', orgId)
    .order('updated_at', { ascending: false });
  if (error) { console.error('loadContacts:', error.message); return []; }
  return data || [];
};

export const loadContact = async (id) => {
  if (!id) return null;
  const { data, error } = await db
    .from('contacts')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) { console.error('loadContact:', error.message); return null; }
  return data;
};

// Best-effort contact lookup by email. Case-insensitive, scoped to
// the org. Used when promoting a CRM deal into a Proposal Coach
// opportunity so the primary_contact_id auto-links to the rich
// contact record (title/phone/mobile/address) instead of leaving
// the AI to draft against just a name + email string.
export const findContactByEmail = async (orgId, email) => {
  if (!orgId || !email) return null;
  const cleaned = String(email).trim();
  if (!cleaned) return null;
  const { data, error } = await db
    .from('contacts')
    .select('*')
    .eq('org_id', orgId)
    .ilike('email', cleaned)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) { console.error('findContactByEmail:', error.message); return null; }
  return (data && data[0]) || null;
};

export const saveContact = async (row, orgId, userId) => {
  const payload = { ...row, org_id: orgId, updated_at: new Date().toISOString() };
  if (!row.id) payload.created_by = userId;

  // Pre-check for duplicate email — the contacts table has a unique
  // (org_id, email) constraint that otherwise fires with a cryptic
  // 23505 "contacts_org_email_uniq" Postgres error. Detecting here
  // lets us return a friendly message the UI can toast, and (if the
  // duplicate is a different row) point the user at the existing
  // contact so they can edit that one instead of creating a copy.
  const emailCleaned = payload.email?.trim();
  if (emailCleaned) {
    const { data: existing } = await db
      .from('contacts')
      .select('id, name, first_name, last_name, email')
      .eq('org_id', orgId)
      .ilike('email', emailCleaned)
      .maybeSingle();
    if (existing && existing.id !== payload.id) {
      const who = existing.name || [existing.first_name, existing.last_name].filter(Boolean).join(' ') || existing.email;
      return {
        error: `A contact with email "${emailCleaned}" already exists in this org (${who}). Edit that contact instead of creating a duplicate.`,
        existingId: existing.id,
      };
    }
  }

  const { data, error } = await db
    .from('contacts')
    .upsert(payload, { onConflict: 'id' })
    .select()
    .single();
  if (error) {
    console.error('saveContact:', error.message);
    // Fallback for anything the pre-check missed (race conditions,
    // other unique constraints). 23505 is Postgres's unique_violation.
    if (error.code === '23505') {
      return { error: 'A contact with this information already exists in your organization.' };
    }
    return { error: error.message || 'Save failed' };
  }
  return { row: data };
};

export const deleteContact = async (id) => {
  const { error } = await db.from('contacts').delete().eq('id', id);
  if (error) console.error('deleteContact:', error.message);
  return !error;
};

// Insert many at once (Gmail import). Returns { inserted, skipped } counts.
// We dedup client-side against existing emails first, then plain-INSERT —
// avoids ON CONFLICT shenanigans against the partial unique index on
// (org_id, lower(email)).
export const bulkInsertContacts = async (rows, orgId, userId) => {
  if (!rows?.length) return { inserted: 0, skipped: 0 };

  const lower = (s) => s ? String(s).trim().toLowerCase() : null;

  // Pull every existing email in this org so we can dedup by lower(email).
  const { data: existingRows, error: existErr } = await db
    .from('contacts')
    .select('email')
    .eq('org_id', orgId);
  if (existErr) {
    console.error('bulkInsertContacts (preload):', existErr.message);
    return { inserted: 0, skipped: 0, error: existErr.message };
  }
  const existing = new Set((existingRows || []).map(r => lower(r.email)).filter(Boolean));

  // Also dedup within the incoming batch itself (Gmail can have multiple
  // person rows pointing at the same email — happens for shared aliases).
  const seenInBatch = new Set();
  const fresh = [];
  let skipped = 0;
  for (const r of rows) {
    const e = lower(r.email);
    if (e && (existing.has(e) || seenInBatch.has(e))) { skipped++; continue; }
    if (e) seenInBatch.add(e);
    fresh.push({ ...r, org_id: orgId, created_by: userId });
  }
  if (!fresh.length) return { inserted: 0, skipped };

  const { data, error } = await db
    .from('contacts')
    .insert(fresh)
    .select('id');
  if (error) {
    console.error('bulkInsertContacts (insert):', error.message);
    return { inserted: 0, skipped, error: error.message };
  }
  return { inserted: (data || []).length, skipped };
};

// Wraps the Vercel API for OCR-ing a business card image.
// `imageData` is a data URL (data:image/jpeg;base64,...) from a FileReader.
export const ocrBusinessCard = async (imageData) => {
  const { data: { session } } = await db.auth.getSession();
  if (!session) return { error: 'not signed in' };
  const resp = await fetch('/api/ocr-business-card', {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      Authorization:   `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ imageData }),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body; // { success, contact, raw }
};

// Wraps /api/google-contacts. Caller passes a Google access token they
// got from Google Identity Services with `contacts.readonly` scope.
export const fetchGoogleContacts = async (googleAccessToken) => {
  const { data: { session } } = await db.auth.getSession();
  if (!session) return { error: 'not signed in' };
  const resp = await fetch('/api/google-contacts', {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      Authorization:   `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ googleAccessToken }),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body; // { success, contacts, total }
};

// ── DEAL ROOM ─────────────────────────────────────────────────────
//
// `deals` are visible across orgs (seller + reviewer company). Helpers
// here are the same shape as the rest of the file — light wrappers that
// log and return either the row(s) or a falsy value.

export const loadDeals = async () => {
  const { data, error } = await db
    .from('deals')
    .select('*')
    .order('updated_at', { ascending: false });
  if (error) { console.error('loadDeals:', error.message); return []; }
  return data || [];
};

export const loadDeal = async (id) => {
  const { data, error } = await db.from('deals').select('*').eq('id', id).maybeSingle();
  if (error) { console.error('loadDeal:', error.message); return null; }
  return data;
};

export const saveDeal = async (row, orgId, userId) => {
  const payload = { ...row, updated_at: new Date().toISOString() };
  if (!row.id) { payload.org_id = orgId; payload.created_by = userId; }
  const { data, error } = await db
    .from('deals')
    .upsert(payload, { onConflict: 'id' })
    .select()
    .single();
  if (error) { console.error('saveDeal:', error.message); return null; }
  return data;
};

// Partial UPDATE on the deals row — only sends the fields in `patch`
// (plus updated_at). Use this in preference to full-row saveDeal()
// when editing an existing deal: protects against schema-mismatch
// failures when the client has stale fields or the DB lacks columns
// the client has been adding to its in-memory state.
// Returns { ok: true } on success, { error: msg } on failure so the
// UI can surface the actual cause.
export const updateDealFields = async (id, patch) => {
  if (!id) return { error: 'updateDealFields: id required' };
  const payload = { ...patch, updated_at: new Date().toISOString() };
  const { error } = await db
    .from('deals')
    .update(payload)
    .eq('id', id);
  if (error) {
    console.error('updateDealFields:', error.message, 'patch keys:', Object.keys(patch || {}));
    return { error: error.message };
  }
  return { ok: true };
};

export const deleteDeal = async (id) => {
  const { error } = await db.from('deals').delete().eq('id', id);
  if (error) console.error('deleteDeal:', error.message);
  return !error;
};

// ── Diligence requests ─────────────────────────────────────────
export const loadDiligenceRequests = async (dealId) => {
  const { data, error } = await db
    .from('diligence_requests')
    .select('*')
    .eq('deal_id', dealId)
    .order('position');
  if (error) { console.error('loadDiligenceRequests:', error.message); return []; }
  return data || [];
};

export const saveDiligenceRequest = async (row) => {
  const payload = { ...row, updated_at: new Date().toISOString() };
  const { data, error } = await db
    .from('diligence_requests')
    .upsert(payload, { onConflict: 'id' })
    .select()
    .single();
  if (error) { console.error('saveDiligenceRequest:', error.message); return null; }
  return data;
};

export const bulkInsertDiligenceRequests = async (dealId, items) => {
  if (!items?.length) return [];
  const rows = items.map((it, i) => ({
    deal_id: dealId,
    request: it.request,
    category: it.category || null,
    position: i,
  }));
  const { data, error } = await db.from('diligence_requests').insert(rows).select();
  if (error) { console.error('bulkInsertDiligenceRequests:', error.message); return []; }
  return data || [];
};

export const deleteDiligenceRequest = async (id) => {
  const { error } = await db.from('diligence_requests').delete().eq('id', id);
  if (error) console.error('deleteDiligenceRequest:', error.message);
  return !error;
};

// ── Files (S3 bucket `deal-room`) ─────────────────────────────
// Object path convention: <deal_id>/<request_id>/<random>-<filename>
// The first segment must be the deal_id — the storage RLS policy reads it.
export const loadDiligenceFiles = async (requestId) => {
  const { data, error } = await db
    .from('diligence_files')
    .select('*')
    .eq('request_id', requestId)
    .order('created_at', { ascending: false });
  if (error) { console.error('loadDiligenceFiles:', error.message); return []; }
  return data || [];
};

// ── Deal Intelligence (seller-only knowledge base) ────────────
// Buyer profile lives in deals.intelligence (a JSONB blob). The
// three child tables (deal_intel_documents / contracts / urls)
// hold background docs, mutual contracts, and crawled URLs.

export const loadDealIntel = async (dealId) => {
  const [{ data: deal }, { data: docs }, { data: contracts }, { data: urls }] = await Promise.all([
    db.from('deals').select('intelligence').eq('id', dealId).maybeSingle(),
    db.from('deal_intel_documents').select('*').eq('deal_id', dealId).order('created_at', { ascending: false }),
    db.from('deal_intel_contracts').select('*').eq('deal_id', dealId).order('created_at', { ascending: false }),
    db.from('deal_intel_urls').select('*').eq('deal_id', dealId).order('created_at', { ascending: false }),
  ]);
  return {
    profile:   deal?.intelligence || {},
    documents: docs || [],
    contracts: contracts || [],
    urls:      urls || [],
  };
};

export const saveDealIntelProfile = async (dealId, profile) => {
  const { error } = await db
    .from('deals')
    .update({ intelligence: profile, updated_at: new Date().toISOString() })
    .eq('id', dealId);
  if (error) { console.error('saveDealIntelProfile:', error.message); return false; }
  return true;
};

export const uploadDealIntelDocument = async (dealId, file, uploadedBy, sourceNote = null) => {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path     = `${dealId}/intel/${Date.now()}-${safeName}`;
  const { error: upErr } = await db.storage
    .from('deal-room')
    .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
  if (upErr) { console.error('uploadDealIntelDocument storage:', upErr.message); return null; }
  const { data, error } = await db
    .from('deal_intel_documents')
    .insert({
      deal_id:      dealId,
      filename:     file.name,
      storage_path: path,
      mime_type:    file.type,
      size_bytes:   file.size,
      source_note:  sourceNote,
      uploaded_by:  uploadedBy,
    })
    .select()
    .single();
  if (error) { console.error('uploadDealIntelDocument row:', error.message); return null; }
  return data;
};

export const deleteDealIntelDocument = async (row) => {
  await db.storage.from('deal-room').remove([row.storage_path]);
  const { error } = await db.from('deal_intel_documents').delete().eq('id', row.id);
  if (error) { console.error('deleteDealIntelDocument:', error.message); return false; }
  return true;
};

export const upsertDealIntelContract = async (dealId, contract) => {
  const payload = {
    ...contract,
    deal_id:    dealId,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await db
    .from('deal_intel_contracts')
    .upsert(payload, { onConflict: 'id' })
    .select()
    .single();
  if (error) { console.error('upsertDealIntelContract:', error.message); return null; }
  return data;
};

export const deleteDealIntelContract = async (id) => {
  const { error } = await db.from('deal_intel_contracts').delete().eq('id', id);
  if (error) { console.error('deleteDealIntelContract:', error.message); return false; }
  return true;
};

export const addDealIntelUrl = async (dealId, url, label = null) => {
  const { data, error } = await db
    .from('deal_intel_urls')
    .insert({ deal_id: dealId, url, label })
    .select()
    .single();
  if (error) {
    console.error('addDealIntelUrl:', error.message);
    return { error: error.message };  // bubble up so UI can surface it
  }
  return { row: data };
};

export const deleteDealIntelUrl = async (id) => {
  const { error } = await db.from('deal_intel_urls').delete().eq('id', id);
  if (error) { console.error('deleteDealIntelUrl:', error.message); return false; }
  return true;
};

// Server endpoints — auth required.
const _bearerHeaders = async () => {
  const { data: { session } } = await db.auth.getSession();
  return {
    'Content-Type':  'application/json',
    Authorization:   `Bearer ${session?.access_token || ''}`,
  };
};

export const fetchDealIntelUrl = async (urlRowId) => {
  const resp = await fetch('/api/deal-intel-fetch-url', {
    method: 'POST',
    headers: await _bearerHeaders(),
    body: JSON.stringify({ url_id: urlRowId }),
  });
  let body = {};
  try { body = await resp.json(); } catch {}
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body;
};

export const setIntelDocumentAiEnabled = async (docId, enable) => {
  const resp = await fetch('/api/deal-intel-prepare-ai', {
    method: 'POST',
    headers: await _bearerHeaders(),
    body: JSON.stringify({ document_id: docId, enable: !!enable }),
  });
  let body = {};
  try { body = await resp.json(); } catch {}
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body;
};

// Toggle a diligence file's AI-readiness. Hits the server endpoint
// which (when enable=true) downloads the bytes from Storage and
// uploads them once to Anthropic's Files API, then stashes the
// returned file_id on the diligence_files row. Chat turns can then
// reference the file by id (KB payload) instead of base64 (MB).
export const setDiligenceFileAiEnabled = async (fileId, enable) => {
  const { data: { session } } = await db.auth.getSession();
  const token = session?.access_token;
  if (!token) return { error: 'not signed in' };
  const resp = await fetch('/api/diligence-file-prepare-ai', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ file_id: fileId, enable: !!enable }),
  });
  let body = {};
  try { body = await resp.json(); } catch {}
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body;
};

// ── Deal Room AI chat ──────────────────────────────────────────
// Each user has their own conversation thread per deal (per-user RLS).
// loadDealAiMessages returns the caller's own history; sendDealAiMessage
// hits the serverless endpoint and lets the server persist both turns.
export const loadDealAiMessages = async (dealId, threadId = null) => {
  let q = db
    .from('deal_ai_messages')
    .select('id, thread_id, role, content, file_ids, model, input_tokens, output_tokens, created_at')
    .eq('deal_id', dealId)
    .order('created_at', { ascending: true });
  if (threadId) q = q.eq('thread_id', threadId);
  const { data, error } = await q;
  if (error) { console.error('loadDealAiMessages:', error.message); return []; }
  return data || [];
};

export const clearDealAiMessages = async (dealId, threadId = null) => {
  let q = db.from('deal_ai_messages').delete().eq('deal_id', dealId);
  if (threadId) q = q.eq('thread_id', threadId);
  const { error } = await q;
  if (error) { console.error('clearDealAiMessages:', error.message); return false; }
  return true;
};

// ── Threads ────────────────────────────────────────────────────
// Each user can have many named conversations per deal. Threads are
// purely per-user (RLS limits to user_id = self).
export const loadDealAiThreads = async (dealId) => {
  const { data, error } = await db
    .from('deal_ai_threads')
    .select('*')
    .eq('deal_id', dealId)
    .order('updated_at', { ascending: false });
  if (error) { console.error('loadDealAiThreads:', error.message); return []; }
  return data || [];
};

export const createDealAiThread = async (dealId, userId, title = 'New conversation') => {
  const { data, error } = await db
    .from('deal_ai_threads')
    .insert({ deal_id: dealId, user_id: userId, title })
    .select()
    .single();
  if (error) { console.error('createDealAiThread:', error.message); return null; }
  return data;
};

export const renameDealAiThread = async (threadId, title) => {
  const { error } = await db
    .from('deal_ai_threads')
    .update({ title, updated_at: new Date().toISOString() })
    .eq('id', threadId);
  if (error) { console.error('renameDealAiThread:', error.message); return false; }
  return true;
};

export const deleteDealAiThread = async (threadId) => {
  // ON DELETE CASCADE on deal_ai_messages.thread_id cleans up the
  // thread's messages automatically.
  const { error } = await db.from('deal_ai_threads').delete().eq('id', threadId);
  if (error) { console.error('deleteDealAiThread:', error.message); return false; }
  return true;
};

// POSTs to /api/deal-ai-chat. The endpoint persists both messages; the
// client just needs to refresh its local list after the call returns.
export const sendDealAiMessage = async ({ dealId, message, fileIds = [], model = null, includeIntelDocs = true, threadId = null }) => {
  const { data: { session } } = await db.auth.getSession();
  const token = session?.access_token;
  if (!token) return { error: 'not signed in' };
  const resp = await fetch('/api/deal-ai-chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      deal_id: dealId,
      message,
      file_ids: fileIds,
      model,
      include_intel_docs: includeIntelDocs,
      thread_id: threadId,
    }),
  });
  let body = {};
  try { body = await resp.json(); } catch {}
  if (!resp.ok) return { error: body.error || `HTTP ${resp.status}` };
  return body; // { reply, model, usage, thread_id }
};

// Bulk-load all files across an array of request ids in one round-trip,
// returning a Map of requestId -> files[] (newest first within each
// request). Powers the Diligence-tab summary counts and the
// "Download all" button so the panel doesn't have to wait for each
// request row to be expanded.
export const loadDealFilesByRequest = async (requestIds) => {
  const out = new Map();
  if (!requestIds?.length) return out;
  const { data, error } = await db
    .from('diligence_files')
    .select('*')
    .in('request_id', requestIds)
    .order('created_at', { ascending: false });
  if (error) { console.error('loadDealFilesByRequest:', error.message); return out; }
  for (const f of data || []) {
    const list = out.get(f.request_id) || [];
    list.push(f);
    out.set(f.request_id, list);
  }
  return out;
};

export const uploadDiligenceFile = async (dealId, requestId, file, uploadedBy) => {
  const safeName  = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path      = `${dealId}/${requestId}/${Date.now()}-${safeName}`;
  const { error: upErr } = await db.storage
    .from('deal-room')
    .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
  if (upErr) { console.error('uploadDiligenceFile (storage):', upErr.message); return null; }
  const { data, error } = await db
    .from('diligence_files')
    .insert({
      request_id:   requestId,
      uploaded_by:  uploadedBy,
      filename:     file.name,
      storage_path: path,
      mime_type:    file.type,
      size_bytes:   file.size,
    })
    .select()
    .single();
  if (error) { console.error('uploadDiligenceFile (row):', error.message); return null; }
  return data;
};

export const deleteDiligenceFile = async (fileRow) => {
  await db.storage.from('deal-room').remove([fileRow.storage_path]);
  const { error } = await db.from('diligence_files').delete().eq('id', fileRow.id);
  if (error) console.error('deleteDiligenceFile:', error.message);
  return !error;
};

// Signed download URL (private bucket). 5-min lifetime is plenty for a click.
export const dealFileDownloadUrl = async (storagePath) => {
  const { data, error } = await db.storage.from('deal-room')
    .createSignedUrl(storagePath, 300);
  if (error) { console.error('dealFileDownloadUrl:', error.message); return null; }
  return data?.signedUrl || null;
};

// ── Comments ──────────────────────────────────────────────────
export const loadDiligenceComments = async (requestId) => {
  const { data, error } = await db
    .from('diligence_comments')
    .select('*')
    .eq('request_id', requestId)
    .order('created_at');
  if (error) { console.error('loadDiligenceComments:', error.message); return []; }
  return data || [];
};

export const addDiligenceComment = async (requestId, userId, content) => {
  const { data, error } = await db
    .from('diligence_comments')
    .insert({ request_id: requestId, user_id: userId, content })
    .select()
    .single();
  if (error) { console.error('addDiligenceComment:', error.message); return null; }
  return data;
};

// ── Deal members ───────────────────────────────────────────────
export const loadDealMembers = async (dealId) => {
  const { data, error } = await db
    .from('deal_members')
    .select('*')
    .eq('deal_id', dealId);
  if (error) { console.error('loadDealMembers:', error.message); return []; }
  return data || [];
};

export const upsertDealMember = async (dealId, userId, role, tabPerms = null) => {
  const row = { deal_id: dealId, user_id: userId, role };
  if (tabPerms !== undefined) row.tab_perms = tabPerms;
  const { error } = await db
    .from('deal_members')
    .upsert(row, { onConflict: 'deal_id,user_id' });
  if (error) console.error('upsertDealMember:', error.message);
  return !error;
};

// ── Deal-tab permissions (per-user / per-deal access control) ──
//
// PERM_TABS lists every gated tab. Diligence has an extra "download"
// dimension; the others are view-only.
export const PERM_TABS = ['diligence', 'orgchart', 'strategy', 'earnout', 'economics'];

// Returns a fully-populated perms object for the given profile + deal +
// optional deal_members row. Sellers and super_admin/admin always get
// everything. Reviewer-company users get the deal's reviewer_default_perms
// unless they have a per-user override. NULL → all-visible (backwards
// compatible).
export function dealEffectivePerms(profile, deal, memberRow) {
  const ALL = {
    diligence: { view: true, download: true },
    orgchart:  { view: true },
    strategy:  { view: true },
    earnout:   { view: true },
    economics: { view: true },
  };
  if (!profile || !deal) return ALL;
  // Staff always sees everything.
  const isStaff = ['super_admin', 'admin'].includes(profile.role);
  if (isStaff) return ALL;
  // A user tagged with the deal's reviewer_company is a REVIEWER — gated
  // by tab_perms even if they technically share the seller's org_id
  // (single-tenant setup: 40Geo users are tagged with company='APEM Group'
  // so they can act as reviewers for the APEM diligence).
  const isReviewer = profile.company && deal.reviewer_company
    && profile.company === deal.reviewer_company;
  // Anyone else in the seller's org is seller-side.
  const isSeller = !isReviewer
    && profile.orgId && deal.org_id && profile.orgId === deal.org_id;
  if (isSeller) return ALL;

  const override = memberRow?.tab_perms;
  const dflt     = deal.reviewer_default_perms;
  const raw      = override || dflt || null;
  if (!raw) return ALL;

  // Fill in missing keys from ALL so the UI never has to ?-check.
  const out = { ...ALL };
  for (const t of PERM_TABS) {
    out[t] = { ...ALL[t], ...(raw[t] || {}) };
  }
  return out;
}

// Save deal-level reviewer defaults.
export const saveReviewerDefaultPerms = async (dealId, perms) => {
  const { error } = await db
    .from('deals')
    .update({ reviewer_default_perms: perms, updated_at: new Date().toISOString() })
    .eq('id', dealId);
  if (error) console.error('saveReviewerDefaultPerms:', error.message);
  return !error;
};

export const removeDealMember = async (dealId, userId) => {
  const { error } = await db
    .from('deal_members')
    .delete()
    .eq('deal_id', dealId)
    .eq('user_id', userId);
  if (error) console.error('removeDealMember:', error.message);
  return !error;
};

// ── USER SESSIONS (login audit + time-in-app) ─────────────────────

export const startUserSession = async (userId, orgId) => {
  const { data, error } = await db
    .from('user_sessions')
    .insert({ user_id: userId, org_id: orgId, user_agent: navigator.userAgent || null })
    .select('id')
    .single();
  if (error) { console.error('startUserSession:', error.message); return null; }
  return data?.id || null;
};

export const heartbeatSession = async (sessionId) => {
  if (!sessionId) return;
  await db.from('user_sessions')
    .update({ last_seen_at: new Date().toISOString() })
    .eq('id', sessionId);
};

export const endSession = async (sessionId) => {
  if (!sessionId) return;
  const now = new Date().toISOString();
  await db.from('user_sessions')
    .update({ ended_at: now, last_seen_at: now })
    .eq('id', sessionId);
};

// Best-effort end on tab close. Uses sendBeacon so it survives unload.
// PATH B — posts to /api/session-end (which stamps ended_at). sendBeacon
// can't attach an auth header, so that endpoint is token-less and
// best-effort; the session id is an unguessable UUID.
export const endSessionBeacon = (sessionId) => {
  if (!sessionId) return;
  try {
    const payload = JSON.stringify({ session_id: sessionId });
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/session-end', new Blob([payload], { type: 'application/json' }));
    } else {
      fetch('/api/session-end', {
        method:    'POST',
        headers:   { 'Content-Type': 'application/json' },
        body:      payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) { /* best-effort */ }
};

// For the admin UI: aggregate "last login" + "time in app over last N days".
export const loadUserSessionStats = async (orgId, days = 30) => {
  const since = new Date(Date.now() - days * 86400_000).toISOString();
  const { data } = await db
    .from('user_sessions')
    .select('user_id, started_at, duration_seconds, ended_at, last_seen_at')
    .eq('org_id', orgId)
    .gte('started_at', since);
  if (!data) return {};
  const out = {};
  for (const s of data) {
    if (!out[s.user_id]) out[s.user_id] = { last_login: null, seconds_30d: 0, session_count: 0 };
    const t = new Date(s.started_at).getTime();
    if (!out[s.user_id].last_login || t > new Date(out[s.user_id].last_login).getTime()) {
      out[s.user_id].last_login = s.started_at;
    }
    out[s.user_id].seconds_30d += s.duration_seconds || 0;
    out[s.user_id].session_count += 1;
  }
  return out;
};

export const loadUserSessions = async (userId, limit = 50) => {
  const { data } = await db
    .from('user_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('started_at', { ascending: false })
    .limit(limit);
  return data || [];
};

// ── ADMIN API WRAPPERS ────────────────────────────────────────────
const callAdminApi = async (path, body) => {
  const { data: { session } } = await db.auth.getSession();
  if (!session) return { error: 'not signed in' };
  const resp = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:   `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) return { error: data.error || `HTTP ${resp.status}` };
  return data;
};

export const adminSetPassword   = (userId, password)        => callAdminApi('/api/admin-set-password',  { user_id: userId, password });
export const adminCreateUser    = (form)                    => callAdminApi('/api/admin-create-user',   form);
export const adminResendInvite  = (userId)                  => callAdminApi('/api/admin-resend-invite', { user_id: userId });
export const adminResetPassword = (userId)                  => callAdminApi('/api/admin-reset-password',{ user_id: userId });
export const adminSetActive     = (userId, isActive)        => callAdminApi('/api/admin-set-active',    { user_id: userId, is_active: !!isActive });
export const adminDeleteUser    = (userId)                  => callAdminApi('/api/admin-delete-user',   { user_id: userId });
// Read-only connection diagnostic — verifies the Keycloak admin service
// account before onboarding. Returns { configured, tokenOk, usersReadOk, ok, error, hint }.
export const adminKeycloakHealth = ()                       => callAdminApi('/api/admin/keycloak-health', {});
// Read-only S3 diagnostic — verifies bucket config + whether existing
// library pointers resolve to real objects. Returns { ok, config, buckets, library, error, hint }.
export const adminStorageHealth  = ()                       => callAdminApi('/api/admin/storage-health', {});
